import { config } from '../config/env'
import { normalizeVehicleDocumentsPayload } from '../utils/mapVehicleRegistryDocuments'
import {
  LeadFiltersAPI,
  UserPhone,
  User,
  DealershipSubscription,
  SubscriptionInvoice,
  VehicleFiltersAPI,
  SaleOnboardingProgress,
  SaleDocumentationChecklistRow,
  SaleCustomerDocumentType,
  SaleContractTemplate,
  DealershipProfileMe,
  SalePreparationDetail,
  SaleOperationalStage,
  SaleOperationalActivityEvent,
  Sale,
  SaleOperationalKanbanColumn,
  Vehicle,
  Customer,
  Notification,
  LeadNote,
} from '../types'
import { WhatsAppTemplate, TemplatesStats } from '../modules/conversations/types'

// Tipos para as respostas da API
interface ApiResponse<T> {
 data: T
 message?: string
 success: boolean
}

export interface FiscalExportPayload {
 sale: Record<string, unknown>
 issuer: Record<string, unknown>
 branch: Record<string, unknown> | null
 customer: Record<string, unknown>
 vehicle: Record<string, unknown>
 items: Array<Record<string, unknown>>
 totals: Record<string, unknown>
 payments: Array<Record<string, unknown>>
 additionalInfo: Record<string, unknown>
}

export interface FiscalExportReadinessResponse {
  ready: boolean
  missingFields: Array<{ key: string; labelKey: string; section: string }>
  export: FiscalExportPayload
}

export interface RegisterVerificationRequiredResponse {
  required: true
  email: string
  expiresAt: string
  message?: string
}

export interface RegisterAuthSessionResponse {
  accessToken: string
  refreshToken: string
  user: {
    id: string
    email: string
    name: string
    context: string
    dealershipId: string
    dealershipRole: string
    backofficeRole?: string
    permissions?: string[]
  }
  dealership: {
    id: string
    cnpj?: string
    cpf?: string
    name: string
    tradeName?: string
    email: string
    phone: string
    address: string
  }
}

export type RegisterResponse = RegisterVerificationRequiredResponse | RegisterAuthSessionResponse

export interface EmailOtpVerificationResponse {
  message?: string
  verified?: boolean
}

export interface CustomerPrimaryAddress {
 id: string
 street: string
 number: string
 complement?: string | null
 neighborhood: string
 city: string
 state: string
 zipCode: string
}

export interface CustomerOriginLead {
 id: string
 name: string
 source: string
 status: string
 convertedAt?: string | null
 message?: string | null
 notes?: string | null
 createdAt: string
}

export interface CustomerRecord {
 id: string
 documentType: 'cpf' | 'cnpj'
 document: string
 name: string
 email?: string | null
 phone?: string | null
 address?: string | null
 notes?: string | null
 primaryAddress?: CustomerPrimaryAddress | null
 originLead?: CustomerOriginLead | null
}

export interface MarketplaceIntegration {
 id: string
 platform: 'OLX' | 'MERCADO_LIVRE' | 'META'
 isActive: boolean
 autoPublish: boolean
 lastSyncAt?: string
 lastError?: string
 createdAt: string
 updatedAt: string
}

export interface VehiclePublication {
 id: string
 vehicleId: string
 integrationId: string
 externalId?: string
 externalUrl?: string
 status: 'PENDING' | 'PUBLISHED' | 'FAILED' | 'UPDATING' | 'PAUSED' | 'REMOVED'
 errorMessage?: string
 publishedAt?: string
 lastSyncAt?: string
 integration?: {
 id: string
 platform: string
 isActive: boolean
 }
}

// WhatsApp Types
export interface WhatsAppConfig {
 id: string
 dealershipId?: string
 phoneNumberId: string
 businessAccountId: string
 accountName?: string | null
 displayPhone?: string
 isPrimary?: boolean
 accessToken?: string
 webhookVerifyToken?: string
 isActive: boolean
 autoReply?: boolean
 autoCreateLead?: boolean
 lastSyncAt?: string | null
 lastError?: string | null
 createdAt: string
 updatedAt?: string
}

export interface WhatsAppOAuthLog {
 id: string
 dealershipId: string
 status: 'success' | 'error' | 'pending'
 step: string
 errorMessage?: string | null
 errorCode?: string | null
 wabaId?: string | null
 phoneNumberId?: string | null
 metadata?: any
 createdAt: string
}

export interface WhatsAppMessage {
 id: string
 configId: string
 leadId?: string | null
 phoneNumber: string
 direction: 'INBOUND' | 'OUTBOUND'
 type: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'TEMPLATE'
 content: string
 mediaUrl?: string | null
 caption?: string | null
 filename?: string | null
 status: 'PENDING' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED' | 'RECEIVED'
 wamId?: string | null
 errorMessage?: string | null
 sentAt?: string | null
 deliveredAt?: string | null
 readAt?: string | null
 createdAt: string
 lead?: {
 id: string
 name: string
 }
 user?: {
 id: string
 name: string
 email?: string
 } | null
}

export type WhatsAppChannelSessionStatus = 'OPEN' | 'TEMPLATE_ONLY' | 'CLOSED'

export interface WhatsAppChannelSession {
 channelSessionStatus: WhatsAppChannelSessionStatus
 allowsSessionMessages: boolean
 allowsTemplates: boolean
 sessionWindowExpiresAt: string | null
 lastCustomerInboundAt: string | null
 finalizedAt: string | null
}

// Lead Distribution Types
export interface LeadDistributionConfig {
 id: string
 dealershipId: string
 strategy: 'ROUND_ROBIN' | 'WEIGHTED' | 'TERRITORY' | 'MANUAL'
 trigger: 'ON_CREATE' | 'ON_STATUS_CHANGE' | 'MANUAL'
 isActive: boolean
 autoReassign: boolean
 sources: string[]
 statuses: string[]
 maxLeadsPerUser?: number | null
 distributionHours?: string | null
 createdAt: string
 updatedAt: string
}

export interface LeadDistributionRule {
 id: string
 configId: string
 userId: string
 weight: number
 priority: number
 maxLeads?: number | null
 territories?: any
 isActive: boolean
 totalAssigned: number
 lastAssignedAt?: string | null
 createdAt: string
 updatedAt: string
 user?: {
 id: string
 name: string
 email: string
 }
}

export interface LeadDistributionStats {
 totalLeads: number
 distributedLeads: number
 manualLeads: number
 byUser: Array<{
 userId: string
 userName: string
 totalAssigned: number
 activeLeads: number
 convertedLeads: number
 conversionRate: number
 }>
 byStrategy: {
 roundRobin: number
 weighted: number
 territory: number
 manual: number
 }
}

export interface DashboardMetrics {
 totalVehicles: number
 totalLeads: number
 totalRevenue: number
 conversionRate: number
 vehiclesTrend: number
 leadsTrend: number
 revenueTrend: number
 conversionTrend: number
}

export interface MonthlySalesData {
 month: string
 vendas: number
 leads: number
}

export interface LeadStatusData {
 name: string
 value: number
 color: string
}

export interface RecentLeadData {
 id: string
 name: string
 email: string
 phone: string
 status: string
 date: string
}

export interface LeadSlaItem {
 hours: number
 target: number
}

export interface SellerLeaderboardItem {
 name: string
 sales: number
 revenue: number
 conversion: number
}

export interface PreparationBottleneckItem {
 stage: string
 count: number
 avgDays: number
}

export interface DashboardOverview {
 metrics: DashboardMetrics
 monthlySales: MonthlySalesData[]
 leadStatus: LeadStatusData[]
 recentLeads: RecentLeadData[]
 stockAging: number[]
 leadSla: LeadSlaItem[]
 sellerLeaderboard: SellerLeaderboardItem[]
 preparationBottlenecks: PreparationBottleneckItem[]
}

export interface FinancialOverview {
 totalRevenue: number
 totalExpenses: number
 netProfit: number
 profitMargin: number
 pendingReceivables: number
 pendingPayables: number
 cashFlow: number
}

export interface CashFlowData {
 period: string
 revenue: number
 expenses: number
 netFlow: number
}

export interface FinancialKPIs {
 averageTicket: number
 defaultRate: number
 operatingMargin: number
 grossMargin: number
 totalSales: number
 totalCosts: number
 totalCharges: number
 totalBenefits: number
}

export interface OverdueInvoice {
 id: string
 number: string
 customerName: string
 amount: number
 dueDate: string
 daysOverdue: number
}

export interface UpcomingPayment {
 id: string
 description: string
 amount: number
 dueDate: string
 daysUntilDue: number
}

export interface PendingFinancials {
 overdueInvoices: OverdueInvoice[]
 upcomingPayments: UpcomingPayment[]
}

export interface PostSalesDashboardMetrics {
 totalDeliveries: number
 pendingDeliveries: number
 completedDeliveries: number
 averageNPS: number
 totalSurveys: number
 promoters: number
 passives: number
 detractors: number
 deliveriesThisMonth: number
 deliveriesTrend: number
}

export interface DeliveryTimeline {
 period: string
 deliveries: number
 avgNPS: number
}

export interface NPSDistribution {
 score: number
 count: number
 percentage: number
}

export interface RecentDelivery {
 id: string
 customerName: string
 vehicleName: string
 deliveryDate: string
 responsibleName: string
 status: string
}

export interface Product {
 id: string
 dealershipId: string
 name: string
 description?: string
 type: 'product' | 'service'
 category: 'accessory' | 'insurance' | 'warranty' | 'maintenance' | 'parts' | 'detailing' | 'protection' | 'documentation' | 'financing' | 'other'
 price: number
 cost?: number
 sku?: string
 isActive: boolean
 supplier?: string
 notes?: string
 createdAt: string
 updatedAt: string
}

export interface ProductStats {
 total: number
 active: number
 inactive: number
 products: number
 services: number
}

export interface MaintenanceRecord {
 id: string
 vehicleId: string
 date: string
 type: 'preventive' | 'corrective' | 'inspection' | 'recall'
 description: string
 cost?: number
 provider?: string
 createdById: string
 createdAt: string
 updatedAt: string
 vehicle?: {
 id: string
 plate: string
 brand?: { name: string }
 model?: { name: string }
 }
 createdBy?: {
 id: string
 name: string
 }
}

export interface MaintenanceSummary {
 total: number
 totalCost: number
 preventive: number
 corrective: number
 inspection: number
 recall: number
 averageCost: number
}

export interface Permission {
 id: string
 name: string
 description?: string
 resource: string
 action: string
 isActive: boolean
 createdAt: string
 _count?: {
 roles: number
 }
}

export interface RolePermission {
 id: string
 roleId: string
 permissionId: string
 permission: Permission
}

export interface Role {
 id: string
 name: string
 description?: string
 context: 'backoffice' | 'dealership'
 isSystem: boolean
 isActive: boolean
 createdAt: string
 updatedAt: string
 permissions: RolePermission[]
 _count?: {
 userRoles: number
 }
}

export interface DealershipAccessPermission {
 id: string
 key: string
 type: 'navigation' | 'api'
 parentKey?: string | null
 path?: string | null
 labelKey?: string | null
 sortOrder: number
 granted: boolean
}

export interface DealershipAccessRoleBlock {
 role: string
 labelKey: string
 editable: boolean
 permissions: DealershipAccessPermission[]
}

export interface DealershipAccessMatrix {
 dealershipId: string
 roles: DealershipAccessRoleBlock[]
 catalog: Array<{
 id: string
 name: string
 type: string
 parentKey?: string | null
 path?: string | null
 labelKey?: string | null
 sortOrder: number
 }>
}

// Classe de erro personalizada
export class ApiError extends Error {
 status?: number

 constructor({ message, status }: { message: string; status?: number }) {
 super(message)
 this.name = 'ApiError'
 this.status = status
 }
}


// Classe para gerenciar chamadas à API
class ApiService {
 private baseUrl: string
 private isRefreshing: boolean = false
 private refreshPromise: Promise<{ accessToken: string; refreshToken: string }> | null = null

 constructor() {
 const trimmed = String(config.api.baseUrl ?? '').trim().replace(/\/+$/, '')
 this.baseUrl = trimmed.length > 0 ? trimmed : 'http://localhost:3000/api'
 }

 private formatErrorResponseMessage(errorData: Record<string, unknown>, status: number): string {
 const rawMessage = errorData.message
 if (Array.isArray(rawMessage)) {
 return rawMessage.filter((item): item is string => typeof item === 'string').join(' ')
 }
 if (typeof rawMessage === 'string' && rawMessage.length > 0) {
 return rawMessage
 }
 return `HTTP error! status: ${status}`
 }

 private appendLeadListQueryParams(
 params: LeadFiltersAPI | undefined,
 queryParams: URLSearchParams,
 ): void {
 if (!params) return
 if (params.page) queryParams.append('page', params.page.toString())
 if (params.limit) queryParams.append('limit', params.limit.toString())
 if (params.status) queryParams.append('status', params.status.trim())
 if (params.source) queryParams.append('source', params.source.trim())
 if (params.search) queryParams.append('search', params.search.trim())
 if (params.phone) queryParams.append('phone', params.phone.trim())
 if (params.name) queryParams.append('name', params.name.trim())
 if (params.email) queryParams.append('email', params.email.trim())
 if (params.document) queryParams.append('document', params.document.trim())
 if (params.assignedTo) queryParams.append('assignedTo', params.assignedTo.trim())
 if (params.vehicleInterest) queryParams.append('vehicleInterest', params.vehicleInterest.trim())
 if (params.vehicleInterestId) queryParams.append('vehicleInterestId', params.vehicleInterestId.trim())
 if (params.funnelStage) queryParams.append('funnelStageId', params.funnelStage.trim())
 if (params.createdAtFrom) queryParams.append('createdAtFrom', params.createdAtFrom)
 if (params.createdAtTo) queryParams.append('createdAtTo', params.createdAtTo)
 if (params.hasProposal !== undefined) queryParams.append('hasProposal', params.hasProposal.toString())
 if (params.hasPreferences !== undefined) queryParams.append('hasPreferences', params.hasPreferences.toString())
 if (params.isConverted !== undefined) queryParams.append('isConverted', params.isConverted.toString())
 if (params.isWhatsApp !== undefined) queryParams.append('isWhatsApp', params.isWhatsApp.toString())
 }

 private async fetchMultipartUpload(
 url: string,
 authHeaders: Record<string, string>,
 formData: FormData,
 ): Promise<Response> {
 const headers: HeadersInit = { ...authHeaders }
 const init: RequestInit = {
 method: 'POST',
 headers,
 body: formData,
 mode: 'cors',
 cache: 'no-store',
 }
 if (
 typeof AbortSignal !== 'undefined' &&
 'timeout' in AbortSignal &&
 typeof AbortSignal.timeout === 'function'
 ) {
 init.signal = AbortSignal.timeout(120_000)
 }
 try {
 return await fetch(url, init)
 } catch (e) {
 if (e instanceof DOMException && e.name === 'AbortError') {
 throw new ApiError({
 message:
 'Tempo limite do envio excedido. Tente de novo; em APIs com cold start (ex.: hospedagem gratuita) aguarde um minuto e repita.',
 status: 0,
 })
 }
 if (e instanceof TypeError) {
 throw new ApiError({
 message:
 'Falha de rede ao enviar o arquivo. Verifique a internet, se a API está no ar (VITE_API_BASE_URL) e CORS no servidor para o domínio desta aplicação.',
 status: 0,
 })
 }
 throw e
 }
 }

 private unwrapApiData<T>(raw: unknown): T {
 if (
 raw !== null &&
 typeof raw === 'object' &&
 'data' in raw &&
 'success' in raw &&
 (raw as { success: unknown }).success === true
 ) {
 return (raw as { data: T }).data
 }
 return raw as T
 }

 private resolveVehicleDetailPayload(raw: unknown): unknown {
 const first = this.unwrapApiData<unknown>(raw)
 if (first !== null && typeof first === 'object' && 'basic' in first) {
 return first
 }
 if (
 first !== null &&
 typeof first === 'object' &&
 'data' in first &&
 (first as { data: unknown }).data !== null &&
 typeof (first as { data: unknown }).data === 'object' &&
 'basic' in ((first as { data: Record<string, unknown> }).data as object)
 ) {
 return (first as { data: unknown }).data
 }
 return first
 }

 private coerceArrayResponse(raw: unknown): unknown[] {
 if (Array.isArray(raw)) {
 return raw
 }
 if (raw !== null && typeof raw === 'object' && 'data' in raw) {
 const inner = (raw as { data: unknown }).data
 if (Array.isArray(inner)) {
 return inner
 }
 }
 return []
 }

 // Método genérico para fazer requisições
 private async request<T>(
 endpoint: string,
 options: RequestInit = {},
 skipRefresh: boolean = false,
 responseKind: 'json' | 'blob' = 'json',
 ): Promise<T> {
 const url = `${this.baseUrl}${endpoint}`
 
 // Adiciona token de autorização se disponível
 const token = localStorage.getItem('accessToken')
 const headers: HeadersInit = {}
 
 // Só adiciona Content-Type se não foi especificado nos options
 if (!options.headers || !('Content-Type' in (options.headers as any))) {
 headers['Content-Type'] = 'application/json'
 }
 
 if (token) {
 headers['Authorization'] = `Bearer ${token}`
 }

 // Merge headers
 const mergedHeaders = {
 ...headers,
 ...(options.headers || {})
 }

 const mergedOptions = { 
 ...options,
 headers: mergedHeaders
 }

 try {
 const response = await fetch(url, mergedOptions)

 // ============================================
 // FLUXO DE REFRESH TOKEN AUTOMÁTICO MELHORADO
 // ============================================
 // Se receber 401 (Unauthorized):
 // 1. Se já há refresh em andamento, aguarda e retenta
 // 2. Se não há refresh, inicia o processo
 // 3. Refaz TODAS as requisições pendentes após refresh bem-sucedido
 // 4. Se falhar a renovação, desloga automaticamente o usuário
 // ============================================
 if (response.status === 401 && !skipRefresh) {
 console.log('🔄 Recebeu 401 - Verificando refresh token...')
 const refreshToken = localStorage.getItem('refreshToken')
 
 if (!refreshToken) {
 console.error('❌ Sem refresh token disponível')
 this.logout()
 throw new ApiError({
 message: 'Sessão expirada. Faça login novamente.',
 status: 401,
 })
 }

 // Se já está fazendo refresh, aguarda o refresh em andamento
 if (this.isRefreshing && this.refreshPromise) {
 console.log('⏳ Refresh já em andamento - Aguardando...')
 try {
 const refreshResponse = await this.refreshPromise
 console.log('✅ Refresh concluído - Retentando requisição...')
 
 // Retry a requisição com o novo token
 const newHeaders = {
 ...headers,
 'Authorization': `Bearer ${refreshResponse.accessToken}`
 }
 
 const retryOptions = {
 ...mergedOptions,
 headers: newHeaders
 }
 
 const retryResponse = await fetch(url, retryOptions)
 
 if (!retryResponse.ok) {
 console.error('❌ Retry falhou após refresh bem-sucedido')
 throw new ApiError({
 message: 'Erro ao processar requisição.',
 status: retryResponse.status,
 })
 }
 
 console.log('✅ Requisição retentada com sucesso')
 return (responseKind === 'blob'
 ? await retryResponse.blob()
 : await retryResponse.json()) as T
 } catch (error) {
 console.error('❌ Erro ao aguardar refresh:', error)
 throw error
 }
 }

 // Inicia novo processo de refresh
 this.isRefreshing = true
 console.log('📡 Iniciando refresh token...')
 
 this.refreshPromise = this.refreshTokenDirect(refreshToken)
 .then(refreshResponse => {
 if (refreshResponse && refreshResponse.accessToken) {
 console.log('✅ Refresh token bem-sucedido')
 
 // Atualiza tokens no localStorage
 localStorage.setItem('accessToken', refreshResponse.accessToken)
 localStorage.setItem('refreshToken', refreshResponse.refreshToken)
 
 return refreshResponse
 } else {
 console.error('❌ Refresh token não retornou tokens válidos')
 this.logout()
 throw new ApiError({
 message: 'Sessão expirada. Faça login novamente.',
 })
 }
 })
 .catch(refreshError => {
 console.error('❌ Erro ao tentar refresh token:', refreshError)
 this.logout()
 throw new ApiError({
 message: 'Sessão expirada. Faça login novamente.',
 })
 })
 .finally(() => {
 this.isRefreshing = false
 this.refreshPromise = null
 })

 try {
 const refreshResponse = await this.refreshPromise
 
 // Retry a requisição original com o novo token
 const newHeaders = {
 ...headers,
 'Authorization': `Bearer ${refreshResponse.accessToken}`
 }
 
 const retryOptions = {
 ...mergedOptions,
 headers: newHeaders
 }
 
 console.log('🔁 Refazendo requisição original com novo token...')
 const retryResponse = await fetch(url, retryOptions)
 
 if (!retryResponse.ok) {
 console.error('❌ Retry da requisição falhou após refresh')
 throw new ApiError({
 message: 'Erro ao processar requisição.',
 status: retryResponse.status,
 })
 }
 
 console.log('✅ Requisição refeita com sucesso')
 return (responseKind === 'blob'
 ? await retryResponse.blob()
 : await retryResponse.json()) as T
 } catch (error) {
 console.error('❌ Erro no processo de refresh:', error)
 throw error
 }
 }

 if (!response.ok) {
 const errorData = (await response.json().catch(() => ({}))) as Record<string, unknown>
 throw new ApiError({
 message: this.formatErrorResponseMessage(errorData, response.status),
 status: response.status,
 })
 }

 if (responseKind === 'blob') {
 return (await response.blob()) as T
 }
 const data = await response.json()
 return data
 } catch (error) {
 if (error instanceof ApiError) {
 throw error
 }
 throw new ApiError({
 message: error instanceof Error ? error.message : 'Network error',
 })
 }
 }

 // Método para GET
 async get<T>(endpoint: string): Promise<T> {
 return this.request<T>(endpoint, {
 method: 'GET',
 })
 }

 async getBlob(endpoint: string): Promise<Blob> {
 return this.request<Blob>(
 endpoint,
 {
 method: 'GET',
 headers: { Accept: 'application/pdf' },
 },
 false,
 'blob',
 )
 }

 // Método para POST
 async post<T>(endpoint: string, data: any, isFormData: boolean = false): Promise<T> {
 const headers: HeadersInit = {}
 
 // Se não for FormData, define Content-Type como JSON
 if (!isFormData && !(data instanceof FormData)) {
 headers['Content-Type'] = 'application/json'
 }

 return this.request<T>(endpoint, {
 method: 'POST',
 headers,
 body: isFormData || data instanceof FormData ? data : JSON.stringify(data),
 })
 }

 // Método para PUT
 async put<T>(endpoint: string, data: any): Promise<T> {
 return this.request<T>(endpoint, {
 method: 'PUT',
 body: JSON.stringify(data),
 })
 }

 // Método para DELETE
 async delete<T>(endpoint: string): Promise<T> {
 return this.request<T>(endpoint, {
 method: 'DELETE',
 })
 }

 // Método para PATCH
 async patch<T>(endpoint: string, data?: any, isFormData: boolean = false): Promise<T> {
 const headers: HeadersInit = {}
 
 // Se não for FormData, define Content-Type como JSON
 if (!isFormData && data && !(data instanceof FormData)) {
 headers['Content-Type'] = 'application/json'
 }

 return this.request<T>(endpoint, {
 method: 'PATCH',
 headers,
 body: data ? (isFormData || data instanceof FormData ? data : JSON.stringify(data)) : undefined,
 })
 }

 // Método para fazer logout
 private logout() {
 console.log('🔒 Fazendo logout automático (sessão expirada)...')
 
 // Dispara evento customizado para que o AuthContext possa escutar
 window.dispatchEvent(new CustomEvent('auth:logout', { 
 detail: { reason: 'session_expired' } 
 }))
 
 // Limpa tokens e dados do usuário
 localStorage.removeItem('accessToken')
 localStorage.removeItem('refreshToken')
 localStorage.removeItem('user')
 localStorage.removeItem('dealership')
 
 // Redireciona para login
 setTimeout(() => {
 window.location.href = '/login'
 }, 100)
 }

 // Método direto para refresh token (sem retry)
 private async refreshTokenDirect(refreshToken: string): Promise<{
 accessToken: string
 refreshToken: string
 }> {
 const url = `${this.baseUrl}/auth/refresh`
 
 try {
 const response = await fetch(url, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json',
 },
 body: JSON.stringify({ refreshToken })
 })

 if (!response.ok) {
 throw new Error(`Refresh token failed: ${response.status}`)
 }

 const data = await response.json()
 return data.data || data
 } catch (error) {
 console.error('❌ Erro ao fazer refresh direto:', error)
 throw error
 }
 }

 // Métodos específicos para endpoints
 async getPlans() {
 return this.get<ApiResponse<any[]>>('/plans')
 }

 async getCurrentSubscription() {
 return this.get<DealershipSubscription>('/subscriptions/current')
 }

 async getSubscriptionInvoices() {
 return this.get<SubscriptionInvoice[]>('/subscriptions/invoices')
 }

 async createSubscriptionCheckout(planId: string, billingPeriod: 'monthly' | 'yearly') {
 return this.post<{ url: string }>('/subscriptions/checkout', { planId, billingPeriod })
 }

 async changeSubscriptionPlan(newPlanId: string, billingPeriod?: 'monthly' | 'yearly') {
 return this.post<DealershipSubscription>('/subscriptions/change-plan', {
 newPlanId,
 billingPeriod,
 })
 }

 async createSubscriptionBillingPortal() {
 return this.post<{ url: string }>('/subscriptions/billing-portal', {})
 }

 async cancelSubscription() {
 return this.post<DealershipSubscription>('/subscriptions/cancel', {})
 }

 async getUserRoles() {
 return this.get<ApiResponse<any[]>>('/users/roles')
 }

 async refreshToken(refreshToken: string) {
 return this.request<ApiResponse<{
 accessToken: string
 refreshToken: string
 }>>('/auth/refresh', {
 method: 'POST',
 body: JSON.stringify({ refreshToken })
 }, true) // skipRefresh = true
 }

 async createUser(data: {
 name: string
 email: string
 password: string
 phones: UserPhone[]
 dealershipRole: string
 isActive?: boolean
 avatar?: File | null
 }) {
 // Se há avatar, usa FormData, senão usa JSON
 if (data.avatar) {
 const formData = new FormData()
 formData.append('name', data.name)
 formData.append('email', data.email)
 formData.append('password', data.password)
 formData.append('phones', JSON.stringify(data.phones))
 formData.append('dealershipRole', data.dealershipRole)
 if (data.isActive !== undefined) {
 formData.append('isActive', data.isActive.toString())
 }
 formData.append('avatar', data.avatar)

 return this.post<ApiResponse<{
 id: string
 name: string
 email: string
 phone: string
 avatar?: string
 role: {
 id: string
 name: string
 description: string
 permissions: string[]
 }
 isActive: boolean
 createdAt: string
 }>>('/users', formData, true) // true para FormData
 } else {
 const { avatar, ...userData } = data
 return this.post<ApiResponse<{
 id: string
 name: string
 email: string
 phone: string
 avatar?: string
 role: {
 id: string
 name: string
 description: string
 permissions: string[]
 }
 isActive: boolean
 createdAt: string
 }>>('/users', userData)
 }
 }

 async getUsers(limit: number = 100, page: number = 1) {
 const response = await this.get<{
 users: Array<{
 id: string
 name: string
 email: string
 phones: Array<UserPhone & {
 id?: string
 createdAt?: string
 updatedAt?: string
 }>
 role: {
 id: string
 name: string
 description: string
 }
 status: 'active' | 'inactive'
 lastLogin?: string
 createdAt: string
 avatar?: string
 dealershipRole?: string
 isActive?: boolean
 }>
 pagination: {
 page: number
 limit: number
 total: number
 totalPages: number
 }
 }>(`/users?limit=${limit}&page=${page}`)
 
 // Remove campos extras dos phones de todos os usuários
 const cleanResponse = {
 ...response,
 users: response.users.map(user => ({
 ...user,
 phones: user.phones.map(phone => ({
 codeArea: phone.codeArea,
 area: phone.area,
 phone: phone.phone,
 isPrimary: phone.isPrimary
 }))
 }))
 }
 
 return cleanResponse
 }

 async disableUser(userId: string) {
 return this.patch<{
 message: string
 user: {
 id: string
 isActive: boolean
 updatedAt: string
 }
 }>(`/users/${userId}/disable`)
 }

 async activateUser(userId: string) {
 return this.patch<{
 message: string
 user: {
 id: string
 isActive: boolean
 updatedAt: string
 }
 }>(`/users/${userId}/activate`)
 }

 async getUserById(userId: string) {
 const response = await this.get<{
 id: string
 name: string
 email: string
 phones: Array<UserPhone & {
 id?: string
 createdAt?: string
 updatedAt?: string
 }>
 role: {
 id: string
 name: string
 description: string
 }
 status: 'active' | 'inactive'
 lastLogin?: string
 createdAt: string
 avatar?: string
 dealershipRole?: string
 isActive?: boolean
 }>(`/users/${userId}`)
 
 // Remove campos extras dos phones
 const cleanResponse = {
 ...response,
 phones: response.phones.map(phone => ({
 codeArea: phone.codeArea,
 area: phone.area,
 phone: phone.phone,
 isPrimary: phone.isPrimary
 }))
 }
 
return cleanResponse
  }

  async updateMyProfile(userData: {
    name?: string
    phones?: UserPhone[]
    avatar?: File | null
  }) {
    const { avatar, ...data } = userData
    return this.patch<{
      message: string
      user: User
    }>('/users/me', data)
  }

  async updateUser(userId: string, userData: {
 name: string
 email: string
 password?: string
 phones: UserPhone[]
 dealershipRole: string
 isActive?: boolean
 avatar?: File | null
 }) {
 // Se há avatar, usa FormData, senão usa JSON
 if (userData.avatar) {
 const formData = new FormData()
 formData.append('name', userData.name)
 formData.append('email', userData.email)
 if (userData.password) {
 formData.append('password', userData.password)
 }
 formData.append('phones', JSON.stringify(userData.phones))
 formData.append('dealershipRole', userData.dealershipRole)
 if (userData.isActive !== undefined) {
 formData.append('isActive', userData.isActive.toString())
 }
 formData.append('avatar', userData.avatar)

 return this.patch<{
 message: string
 user: {
 id: string
 name: string
 email: string
 phones: UserPhone[]
 avatar?: string
 role: {
 id: string
 name: string
 description: string
 }
 status: 'active' | 'inactive'
 isActive: boolean
 updatedAt: string
 }
 }>(`/users/${userId}`, formData, true) // true para FormData
 } else {
 const { avatar, ...data } = userData
 return this.patch<{
 message: string
 user: {
 id: string
 name: string
 email: string
 phones: UserPhone[]
 avatar?: string
 role: {
 id: string
 name: string
 description: string
 }
 status: 'active' | 'inactive'
 isActive: boolean
 updatedAt: string
 }
 }>(`/users/${userId}`, data)
 }
 }

 // Catalog APIs
 async getBrands(category?: string) {
 const queryString = category ? `?category=${category}` : ''
 return this.get<Array<{
 id: string
 name: string
 logoUrl?: string
 }>>(`/catalog/brands${queryString}`)
 }

 async getBrandById(brandId: string) {
 return this.get<{
 id: string
 name: string
 logoUrl?: string
 }>(`/catalog/brands/${brandId}`)
 }

 async getBrandModels(brandId: string) {
    return this.get<Array<{
      id: string
      name: string
      brandId: string
      startYear?: number
      endYear?: number
    }>>(`/catalog/brands/${brandId}/models`)
 }

 async getModelById(modelId: string) {
    return this.get<{
      id: string
      name: string
      brandId: string
      startYear?: number
      endYear?: number
      brand: {
        id: string
        name: string
      }
    }>(`/catalog/models/${modelId}`)
 }

 async getModelVersions(modelId: string) {
 return this.get<Array<{
 id: string
 name: string
 modelId: string
 year: number
 fuelType: string
 transmission: string
 }>>(`/catalog/models/${modelId}/versions`)
 }

 async getVersionById(versionId: string) {
 return this.get<{
 id: string
 name: string
 modelId: string
 year: number
 fuelType: string
 transmission: string
 model: {
 id: string
 name: string
 brand: {
 id: string
 name: string
 }
 }
 }>(`/catalog/versions/${versionId}`)
 }

 async getVersionYears(versionId: string) {
 return this.get<Array<{
 id: string
 versionId: string
 yearId: string
 createdAt: string
 updatedAt: string
 year: {
 id: string
 year: number
 productionStatus: string
 isActive: boolean
 createdAt: string
 updatedAt: string
 }
 }>>(`/catalog/versions/${versionId}/years`)
 }

 async getFipeValue(versionId: string, yearId: string) {
 return this.get<{
 value: string
 numericValue: number
 brand: string
 model: string
 year: number
 fuelType: string
 referenceMonth: string
 queriedAt: string
 }>(`/catalog/versions/${versionId}/years/${yearId}/fipe`)
 }

 async searchCatalog(query: string) {
    return this.get<{
      brands: Array<{
        id: string
        name: string
        logo?: string
      }>
      models: Array<{
        id: string
        name: string
        brandId: string
        startYear?: number
        endYear?: number
        brand: {
          id: string
          name: string
        }
      }>
      versions: Array<{
        id: string
        name: string
        modelId: string
        year: number
        fuelType: string
        transmission: string
        model: {
          id: string
          name: string
          brand: {
            id: string
            name: string
          }
        }
      }>
    }>(`/catalog/search?q=${encodeURIComponent(query)}`)
 }


 // ===== ROTAS GERAIS DE OPTIONS =====
 
 async getVehicleCategories() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/categories')
 }

 async getVehicleTypes() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/types')
 }

 async getFuelTypes() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/fuel-types')
 }

 async getTransmissions() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/transmissions')
 }

 async getVehicleConditions() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/conditions')
 }

 async getVehicleStatuses() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/statuses')
 }

 async getArmorLevels() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/armor-levels')
 }

 async getSpecialCategories() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/special-categories')
 }

 async getSteeringTypes() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/steering-types')
 }

 async getVehicleCharacteristics() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/characteristics')
 }

 async getVehicleColors() {
 return this.get<Array<{ value: string; label: string; hex?: string }>>('/vehicles/options/colors')
 }

 async getDoorOptions() {
 return this.get<Array<{ value: number; label: string }>>('/vehicles/options/doors')
 }

 async getDamageLevels() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/damage-levels')
 }

 // ===== ROTAS DE LEILÕES =====

 async getAuctionCategories() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/auctions/categories')
 }

 async getAuctionInsurance() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/auctions/insurance')
 }

 async getAuctionBank() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/auctions/bank')
 }

 async getAuctionJudicial() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/auctions/judicial')
 }

 async getAuctionFederal() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/auctions/federal')
 }

 async getAuctionPolice() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/auctions/police')
 }

 async getAuctionFleet() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/auctions/fleet')
 }

 async getAuctionPrivate() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/auctions/private')
 }

 async getAllAuctionOptions() {
 return this.get<{
 categories: Array<{ value: string; label: string }>
 insurance: Array<{ value: string; label: string }>
 bank: Array<{ value: string; label: string }>
 judicial: Array<{ value: string; label: string }>
 federal: Array<{ value: string; label: string }>
 police: Array<{ value: string; label: string }>
 fleet: Array<{ value: string; label: string }>
 private: Array<{ value: string; label: string }>
 }>('/vehicles/options/auctions/all')
 }

 // ===== ROTAS DE MOTOS =====

 async getMotorcycleEngineTypes() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/motorcycles/engine-types')
 }

 async getMotorcycleCoolingTypes() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/motorcycles/cooling-types')
 }

 async getMotorcycleStarterTypes() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/motorcycles/starter-types')
 }

 async getMotorcycleBrakeTypes() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/motorcycles/brake-types')
 }

 async getMotorcycleFeedingTypes() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/motorcycles/feeding-types')
 }

 async getMotorcycleStyles() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/motorcycles/styles')
 }

 async getMotorcycleCategories() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/motorcycles/categories')
 }

 async getMotorcycleCylinderCapacities() {
 return this.get<Array<{ value: string; label: string }>>('/vehicles/options/motorcycles/cylinder-capacities')
 }

 async getAllMotorcycleOptions() {
 return this.get<{
 engineTypes: Array<{ value: string; label: string }>
 coolingTypes: Array<{ value: string; label: string }>
 starterTypes: Array<{ value: string; label: string }>
 brakeTypes: Array<{ value: string; label: string }>
 feedingTypes: Array<{ value: string; label: string }>
 styles: Array<{ value: string; label: string }>
 categories: Array<{ value: string; label: string }>
 cylinderCapacities: Array<{ value: string; label: string }>
 }>('/vehicles/options/motorcycles/all')
 }

 // ===== ROTA COMPLETA =====

 async getAllVehicleOptions() {
 type VehicleOptionRow = { value: string; label: string; meta?: unknown }
 type VehicleFeatureGroupsResponse = {
 comfort: VehicleOptionRow[]
 safety: VehicleOptionRow[]
 technology: VehicleOptionRow[]
 convenience: VehicleOptionRow[]
 performance: VehicleOptionRow[]
 lighting: VehicleOptionRow[]
 adas: VehicleOptionRow[]
 }
 return this.get<{
 // Gerais
 categories: Array<{ value: string; label: string }>
 types: Array<{ value: string; label: string }>
 fuelTypes: Array<{ value: string; label: string }>
 transmissions: Array<{ value: string; label: string }>
 conditions: Array<{ value: string; label: string }>
 statuses: Array<{ value: string; label: string }>
 armorLevels: Array<{ value: string; label: string }>
 specialCategories: Array<{ value: string; label: string }>
 steeringTypes: Array<{ value: string; label: string }>
 characteristics: Array<{ value: string; label: string }>
 segmentTypes: Array<{ value: string; label: string }>
 /** Equipamentos por categoria CNH A–D (moto, carro, caminhão, ônibus) */
 features: Record<'A' | 'B' | 'C' | 'D', VehicleFeatureGroupsResponse>
 colors: Array<{ value: string; label: string; hex?: string }>
 doors: Array<{ value: number; label: string }>
 damageLevels: Array<{ value: string; label: string }>
 
 // Leilões
 auctions: {
 categories: Array<{ value: string; label: string }>
 damageLevels: Array<{ value: string; label: string }>
 insurance: Array<{ value: string; label: string }>
 bank: Array<{ value: string; label: string }>
 judicial: Array<{ value: string; label: string }>
 federal: Array<{ value: string; label: string }>
 police: Array<{ value: string; label: string }>
 fleet: Array<{ value: string; label: string }>
 private: Array<{ value: string; label: string }>
 }
 
 // Motos
 motorcycles: {
 engineTypes: Array<{ value: string; label: string }>
 coolingTypes: Array<{ value: string; label: string }>
 starterTypes: Array<{ value: string; label: string }>
 brakeTypes: Array<{ value: string; label: string }>
 feedingTypes: Array<{ value: string; label: string }>
 styles: Array<{ value: string; label: string }>
 categories: Array<{ value: string; label: string }>
 cylinderCapacities: Array<{ value: string; label: string }>
 }
 // Caminhões
 trucks: {
 bodyTypes: Array<{ value: string; label: string }>
 cabTypes: Array<{ value: string; label: string }>
 tractionTypes: Array<{ value: string; label: string }>
 }
 // Ônibus
 buses: {
 bodyBrands: Array<{ value: string; label: string }>
 operationTypes: Array<{ value: string; label: string }>
 equipment: Array<{ value: string; label: string }>
 }
 }>('/vehicles/options/all')
 }


 async login(credentials: { email: string; password: string }) {
 return this.request<ApiResponse<{
 accessToken: string
 refreshToken: string
 user: {
 id: string
 email: string
 name: string
 context: string
 dealershipId: string
 dealershipRole: string
 backofficeRole?: string
 permissions?: string[]
 }
 dealership: {
 id: string
 cnpj?: string
 cpf?: string
 name: string
 tradeName?: string
 email: string
 phone: string
 address: string
 }
 }>>('/auth/login', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(credentials),
 }, true) // skipRefresh: credenciais inválidas não devem disparar refresh
 }

 async register(data: {
   name: string
   email: string
   password: string
   planId: string
   country: string
   cpf?: string
   cnpj?: string
   taxId?: string
   dealershipName: string
   tradeName?: string
   dealershipEmail: string
   phone: string
   address: {
     zipCode: string
     street: string
     number: string
     complement?: string
     neighborhood: string
     city: string
     state: string
   }
 }): Promise<ApiResponse<RegisterResponse>> {
   return this.post<ApiResponse<RegisterResponse>>('/auth/register', data)
 }

 async verifyEmailOtp(data: { email: string; otp: string }): Promise<ApiResponse<EmailOtpVerificationResponse>> {
   return this.post<ApiResponse<EmailOtpVerificationResponse>>('/auth/verify-email-otp', data)
 }

 async resendEmailOtp(data: { email: string }): Promise<ApiResponse<{ message?: string; sent?: boolean; email?: string; expiresAt?: string }>> {
   return this.post<ApiResponse<{ message?: string; sent?: boolean; email?: string; expiresAt?: string }>>('/auth/resend-email-otp', data)
 }

 // ==================== VEHICLES ====================
 
 // Método auxiliar para upload de FormData com suporte a refresh token
 private async uploadFormData<T>(endpoint: string, formData: FormData): Promise<T> {
 const url = `${this.baseUrl}${endpoint}`
 const token = localStorage.getItem('accessToken')
 const authHeaders: Record<string, string> = token
 ? { Authorization: `Bearer ${token}` }
 : {}

 const response = await this.fetchMultipartUpload(url, authHeaders, formData)

 // Interceptor de refresh token para FormData (mesmo sistema de fila)
 if (response.status === 401) {
 console.log('🔄 Upload recebeu 401 - Verificando refresh token...')
 const refreshToken = localStorage.getItem('refreshToken')
 
 if (!refreshToken) {
 console.error('❌ Sem refresh token disponível')
 this.logout()
 throw new ApiError({
 message: 'Sessão expirada. Faça login novamente.',
 status: 401,
 })
 }

 // Se já está fazendo refresh, aguarda o refresh em andamento
 if (this.isRefreshing && this.refreshPromise) {
 console.log('⏳ Refresh já em andamento - Aguardando para retentar upload...')
 try {
 const refreshResponse = await this.refreshPromise
 console.log('✅ Refresh concluído - Retentando upload...')
 
 const retryResponse = await this.fetchMultipartUpload(url, {
 Authorization: `Bearer ${refreshResponse.accessToken}`,
 }, formData)
 
 if (!retryResponse.ok) {
 console.error('❌ Retry do upload falhou')
 throw new ApiError({
 message: 'Erro ao fazer upload.',
 status: retryResponse.status,
 })
 }
 
 console.log('✅ Upload retentado com sucesso')
 return await retryResponse.json()
 } catch (error) {
 console.error('❌ Erro ao aguardar refresh no upload:', error)
 throw error
 }
 }

 // Inicia novo processo de refresh (se não houver um em andamento)
 if (!this.isRefreshing) {
 this.isRefreshing = true
 console.log('📡 Iniciando refresh token para upload...')
 
 this.refreshPromise = this.refreshTokenDirect(refreshToken)
 .then(refreshResponse => {
 if (refreshResponse && refreshResponse.accessToken) {
 console.log('✅ Refresh token bem-sucedido')
 
 localStorage.setItem('accessToken', refreshResponse.accessToken)
 localStorage.setItem('refreshToken', refreshResponse.refreshToken)
 
 return refreshResponse
 } else {
 console.error('❌ Refresh token não retornou tokens válidos')
 this.logout()
 throw new ApiError({
 message: 'Sessão expirada. Faça login novamente.',
 })
 }
 })
 .catch(refreshError => {
 console.error('❌ Erro ao tentar refresh token:', refreshError)
 this.logout()
 throw new ApiError({
 message: 'Sessão expirada. Faça login novamente.',
 })
 })
 .finally(() => {
 this.isRefreshing = false
 this.refreshPromise = null
 })

 try {
 const refreshResponse = await this.refreshPromise
 
 const retryResponse = await this.fetchMultipartUpload(url, {
 Authorization: `Bearer ${refreshResponse.accessToken}`,
 }, formData)
 
 if (!retryResponse.ok) {
 console.error('❌ Retry do upload falhou após refresh')
 throw new ApiError({
 message: 'Erro ao fazer upload.',
 status: retryResponse.status,
 })
 }
 
 console.log('✅ Upload refeito com sucesso')
 return await retryResponse.json()
 } catch (error) {
 console.error('❌ Erro no processo de refresh do upload:', error)
 throw error
 }
 }
 }

 if (!response.ok) {
 const errorData = (await response.json().catch(() => ({}))) as Record<string, unknown>
 throw new ApiError({
 message: this.formatErrorResponseMessage(errorData, response.status),
 status: response.status,
 })
 }

 return await response.json()
 }

 async uploadTempVehiclePhotos(formData: FormData) {
 return this.uploadFormData<any>('/vehicles/photos/upload-temp', formData)
 }

 async uploadTempVehicleDocuments(formData: FormData) {
 return this.uploadFormData<any>('/vehicles/documents/upload-temp', formData)
 }

 createVehicle(data: any) {
 return this.request<{
 id: string
 message: string
 }>('/vehicles', {
 method: 'POST',
 body: JSON.stringify(data)
 })
 }

 getVehicles(params?: VehicleFiltersAPI) {
 const queryParams = new URLSearchParams()
 
if (params?.page) queryParams.append('page', params.page.toString())
  if (params?.limit) queryParams.append('limit', params.limit.toString())
  if (params?.search) queryParams.append('search', params.search)
  if (params?.name) queryParams.append('name', params.name)
  if (params?.status) queryParams.append('status', params.status)
 if (params?.category) queryParams.append('category', params.category)
 if (params?.condition) queryParams.append('condition', params.condition)
 if (params?.brandId) queryParams.append('brandId', params.brandId)
 if (params?.modelId) queryParams.append('modelId', params.modelId)
 if (params?.versionId) queryParams.append('versionId', params.versionId)
 if (params?.minYear) queryParams.append('minYear', params.minYear.toString())
 if (params?.maxYear) queryParams.append('maxYear', params.maxYear.toString())
 if (params?.minMileage) queryParams.append('minMileage', params.minMileage.toString())
 if (params?.maxMileage) queryParams.append('maxMileage', params.maxMileage.toString())
 if (params?.fuelType) queryParams.append('fuelType', params.fuelType)
 if (params?.plateEnd) queryParams.append('plateEnd', params.plateEnd)
 if (params?.armorLevel) queryParams.append('armorLevel', params.armorLevel)
 if (params?.color) queryParams.append('color', params.color)
 if (params?.type) queryParams.append('type', params.type)
 if (params?.doors) queryParams.append('doors', params.doors.toString())
 if (params?.isAuction !== undefined) queryParams.append('isAuction', params.isAuction.toString())
 if (params?.specialCategory) queryParams.append('specialCategory', params.specialCategory)
 if (params?.characteristics && params.characteristics.length > 0) {
 params.characteristics.forEach(char => queryParams.append('characteristics[]', char))
 }
 
 const queryString = queryParams.toString()
 const endpoint = queryString ? `/vehicles?${queryString}` : '/vehicles'

 return this.get<unknown>(endpoint).then((raw) => {
 const body = this.unwrapApiData<{
 vehicles?: unknown[]
 pagination?: { page: number; limit: number; total: number; pages: number }
 }>(raw)
 const vehicles = Array.isArray(body?.vehicles) ? (body.vehicles as Vehicle[]) : []
 const pagination =
 body?.pagination ??
 ({
 page: params?.page ?? 1,
 limit: params?.limit ?? 50,
 total: vehicles.length,
 pages: vehicles.length > 0 ? 1 : 0,
 } as { page: number; limit: number; total: number; pages: number })
 return { vehicles, pagination }
 })
 }

 getVehicleById(vehicleId: string) {
 return this.get<unknown>(`/vehicles/${vehicleId}`).then((raw) =>
 this.resolveVehicleDetailPayload(raw),
 )
 }

 updateVehicle(vehicleId: string, data: any) {
 return this.request<{
 id: string
 message: string
 }>(`/vehicles/${vehicleId}`, {
 method: 'PATCH',
 body: JSON.stringify(data)
 })
 }

 deleteVehicle(vehicleId: string) {
 return this.delete(`/vehicles/${vehicleId}`)
 }

 getVehicleHistory(vehicleId: string) {
 return this.get<any[]>(`/vehicles/${vehicleId}/history`)
 }

 // ==================== LEADS ====================
 
 getLeadOptions() {
 return this.get<{
 statuses: Array<{ value: string; label: string; color?: string }>
 sources: Array<{ value: string; label: string; color?: string }>
 funnelStages?: Array<{ value: string; label: string; color?: string }>
 }>('/leads/options/all')
 }

 getLeads(params?: LeadFiltersAPI) {
 const queryParams = new URLSearchParams()
 this.appendLeadListQueryParams(params, queryParams)

 const queryString = queryParams.toString()
 const endpoint = queryString ? `/leads?${queryString}` : '/leads'
 
 return this.get<{
 leads: any[]
 pagination: {
 page: number
 limit: number
 total: number
 totalPages: number
 }
 }>(endpoint)
 }

 downloadLeadsReportCsv(params?: LeadFiltersAPI): Promise<Blob> {
 const queryParams = new URLSearchParams()
 this.appendLeadListQueryParams(params, queryParams)
 const queryString = queryParams.toString()
 const endpoint = queryString ? `/leads/report/export?${queryString}` : '/leads/report/export'
 return this.request<Blob>(endpoint, { method: 'GET' }, false, 'blob')
 }

 checkLeadByPhone(phone: string) {
 return this.get<{
 exists: boolean
 lead?: any
 }>(`/leads/check-phone?phone=${encodeURIComponent(phone)}`)
 }

checkLeadByEmail(email: string) {
    return this.get<{
      exists: boolean
      lead?: any
    }>(`/leads/check-email?email=${encodeURIComponent(email)}`)
  }

  checkLeadByDocument(document: string) {
    return this.get<{
      exists: boolean
      lead?: any
    }>(`/leads/check-document?document=${encodeURIComponent(document)}`)
  }

getLeadById(leadId: string) {
    return this.get<any>(`/leads/${leadId}`)
  }

  getLead(leadId: string) {
    return this.getLeadById(leadId)
  }

  createLead(data: any) {
 return this.request<any>('/leads', {
 method: 'POST',
 body: JSON.stringify(data)
 })
 }

 updateLead(leadId: string, data: any) {
 return this.request<any>(`/leads/${leadId}`, {
 method: 'PATCH',
 body: JSON.stringify(data)
 })
 }

 deleteLead(leadId: string) {
 return this.delete(`/leads/${leadId}`)
 }

 getLeadHistory(leadId: string) {
 return this.get<any[]>(`/leads/${leadId}/history`)
 }

 getLeadNotes(leadId: string) {
 return this.get<LeadNote[]>(`/leads/${leadId}/notes`)
 }

 addLeadNote(leadId: string, content: string) {
 return this.request<LeadNote>(`/leads/${leadId}/notes`, {
 method: 'POST',
 body: JSON.stringify({ content })
 })
 }

 // ==================== SALES FUNNELS ====================
 
 getSalesFunnels() {
 return this.get<any[]>('/sales-funnels')
 }

 getDefaultSalesFunnel() {
 return this.get<any>('/sales-funnels/default')
 }

 getSalesFunnelById(funnelId: string) {
 return this.get<any>(`/sales-funnels/${funnelId}`)
 }

 createSalesFunnel(data: any) {
 return this.request<any>('/sales-funnels', {
 method: 'POST',
 body: JSON.stringify(data)
 })
 }

 updateSalesFunnel(funnelId: string, data: any) {
 return this.request<any>(`/sales-funnels/${funnelId}`, {
 method: 'PATCH',
 body: JSON.stringify(data)
 })
 }

 deleteSalesFunnel(funnelId: string) {
 return this.request<void>(`/sales-funnels/${funnelId}`, {
 method: 'DELETE'
 })
 }

 // Funnel Stages
 createFunnelStage(funnelId: string, data: any) {
 return this.request<any>(`/sales-funnels/${funnelId}/stages`, {
 method: 'POST',
 body: JSON.stringify(data)
 })
 }

 updateFunnelStage(funnelId: string, stageId: string, data: any) {
 return this.request<any>(`/sales-funnels/${funnelId}/stages/${stageId}`, {
 method: 'PATCH',
 body: JSON.stringify(data)
 })
 }

 deleteFunnelStage(funnelId: string, stageId: string) {
 return this.request<void>(`/sales-funnels/${funnelId}/stages/${stageId}`, {
 method: 'DELETE'
 })
 }

 updateFunnelStagesOrder(funnelId: string, data: any) {
 return this.request<any>(`/sales-funnels/${funnelId}/stages/reorder`, {
 method: 'PATCH',
 body: JSON.stringify(data)
 })
 }

 // ==================== PROPOSALS ====================
 
 getProposalOptions() {
 return this.get<{
 statuses: Array<{ value: string; label: string }>
 paymentMethods: Array<{ value: string; label: string }>
 }>('/proposals/options/all')
 }

 getProposalStatuses() {
 return this.get<Array<{ value: string; label: string }>>('/proposals/statuses')
 }

 getProposals(params?: {
 page?: number
 limit?: number
 status?: string
 startDate?: string
 endDate?: string
 validUntilStart?: string
 validUntilEnd?: string
 customerId?: string
 vehicleId?: string
 search?: string
 }) {
 const queryParams = new URLSearchParams()
 
 if (params?.page) queryParams.append('page', params.page.toString())
 if (params?.limit) queryParams.append('limit', params.limit.toString())
 if (params?.status) queryParams.append('status', params.status)
 if (params?.startDate) queryParams.append('startDate', params.startDate)
 if (params?.endDate) queryParams.append('endDate', params.endDate)
 if (params?.validUntilStart) queryParams.append('validUntilStart', params.validUntilStart)
 if (params?.validUntilEnd) queryParams.append('validUntilEnd', params.validUntilEnd)
 if (params?.customerId) queryParams.append('customerId', params.customerId)
 if (params?.vehicleId) queryParams.append('vehicleId', params.vehicleId)
 if (params?.search) queryParams.append('search', params.search)
 
 const queryString = queryParams.toString()
 const endpoint = queryString ? `/proposals?${queryString}` : '/proposals'
 
 return this.get<{
 proposals: any[]
 pagination: {
 page: number
 limit: number
 total: number
 totalPages: number
 }
 }>(endpoint)
 }

 getProposalById(proposalId: string) {
 return this.get<any>(`/proposals/${proposalId}`)
 }

 createProposal(data: any) {
 return this.request<any>('/proposals', {
 method: 'POST',
 body: JSON.stringify(data)
 })
 }

 updateProposal(proposalId: string, data: any) {
 return this.request<any>(`/proposals/${proposalId}`, {
 method: 'PATCH',
 body: JSON.stringify(data)
 })
 }

 deleteProposal(proposalId: string) {
 return this.delete(`/proposals/${proposalId}`)
 }

 approveProposal(proposalId: string, data?: { approvalNotes?: string; approvedAt?: string }) {
 return this.request<any>(`/proposals/${proposalId}/approve`, {
 method: 'POST',
 body: JSON.stringify(data || {})
 })
 }

 rejectProposal(proposalId: string) {
 return this.request<any>(`/proposals/${proposalId}/reject`, {
 method: 'POST',
 body: JSON.stringify({})
 })
 }

 addProposalCost(proposalId: string, data: { description: string; estimatedAmount: number }) {
 return this.request<any>(`/proposals/${proposalId}/costs`, {
 method: 'POST',
 body: JSON.stringify(data)
 })
 }

 addSalePayment(
 saleId: string,
 data: {
 amount: number
 method?: string
 paymentFormId?: string
 paymentOptionId?: string
 paidAt?: string
 status?: 'pending' | 'confirmed'
 }
 ) {
 return this.request<any>(`/sales/${saleId}/payments`, {
 method: 'POST',
 body: JSON.stringify(data)
 })
 }

 updateSalePayment(
 saleId: string,
 paymentId: string,
 data: {
 amount?: number
 method?: string
 paymentFormId?: string | null
 paymentOptionId?: string | null
 status?: 'pending' | 'confirmed' | 'cancelled'
 paidAt?: string | null
 }
 ) {
 return this.request<any>(`/sales/${saleId}/payments/${paymentId}`, {
 method: 'PATCH',
 body: JSON.stringify(data)
 })
 }

 deleteSalePayment(saleId: string, paymentId: string) {
 return this.request<any>(`/sales/${saleId}/payments/${paymentId}`, {
 method: 'DELETE'
 })
 }

 splitSalePayment(
 saleId: string,
 data: {
 sourcePaymentId: string
 parts: Array<{
 amount: number
 paymentFormId?: string
 paymentOptionId?: string
 method?: string
 status?: 'pending' | 'confirmed'
 paidAt?: string | null
 }>
 }
 ) {
 return this.request<any>(`/sales/${saleId}/payments/split`, {
 method: 'POST',
 body: JSON.stringify(data)
 })
 }

 updateSaleCharge(
 saleId: string,
 saleChargeId: string,
 data: { value?: number; discountPercentage?: number | null }
 ) {
 return this.request<any>(`/sales/${saleId}/charges/${saleChargeId}`, {
 method: 'PATCH',
 body: JSON.stringify(data)
 })
 }

 addSaleCost(saleId: string, data: { description: string; amount: number; type: string }) {
 return this.request<any>(`/sales/${saleId}/costs`, {
 method: 'POST',
 body: JSON.stringify(data)
 })
 }

 markSaleInPreparation(saleId: string) {
 return this.request<any>(`/sales/${saleId}/prepare`, {
 method: 'POST',
 body: JSON.stringify({})
 })
 }

 markSaleReadyForDelivery(saleId: string) {
 return this.request<any>(`/sales/${saleId}/ready`, {
 method: 'POST',
 body: JSON.stringify({})
 })
 }

 deliverSale(saleId: string) {
 return this.request<any>(`/sales/${saleId}/deliver`, {
 method: 'POST',
 body: JSON.stringify({})
 })
 }

 activateVehicle(vehicleId: string) {
 return this.request<any>(`/vehicles/${vehicleId}/activate`, {
 method: 'POST',
 body: JSON.stringify({})
 })
 }

 // ==================== FINANCIAL CHARGES ====================
 
 getFinancialChargeOptions() {
 return this.get<{
 types: Array<{ value: string; label: string }>
 categories: Array<{ value: string; label: string; color: string }>
 }>('/financial-charges/options/all')
 }

 getFinancialCharges(params?: {
 page?: number
 limit?: number
 vehicleId?: string
 type?: string
 category?: string
 search?: string
 }) {
 const queryParams = new URLSearchParams()
 
 if (params?.page) queryParams.append('page', params.page.toString())
 if (params?.limit) queryParams.append('limit', params.limit.toString())
 if (params?.vehicleId) queryParams.append('vehicleId', params.vehicleId)
 if (params?.type) queryParams.append('type', params.type)
 if (params?.category) queryParams.append('category', params.category)
 if (params?.search) queryParams.append('search', params.search)
 
 const queryString = queryParams.toString()
 const endpoint = queryString ? `/financial-charges?${queryString}` : '/financial-charges'
 
 return this.get<{
 charges: any[]
 pagination: {
 page: number
 limit: number
 total: number
 totalPages: number
 }
 }>(endpoint)
 }

 getFinancialChargeById(chargeId: string) {
 return this.get<any>(`/financial-charges/${chargeId}`)
 }

 createFinancialCharge(data: any) {
 return this.request<any>('/financial-charges', {
 method: 'POST',
 body: JSON.stringify(data)
 })
 }

 updateFinancialCharge(chargeId: string, data: any) {
 return this.request<any>(`/financial-charges/${chargeId}`, {
 method: 'PATCH',
 body: JSON.stringify(data)
 })
 }

 deleteFinancialCharge(chargeId: string) {
 return this.delete(`/financial-charges/${chargeId}`)
 }

 // Financial Charge Categories

 // ===== BENEFITS =====
 getBenefits(params?: {
 page?: number
 limit?: number
 search?: string
 }) {
 const queryParams = new URLSearchParams()
 if (params?.page) queryParams.append('page', params.page.toString())
 if (params?.limit) queryParams.append('limit', params.limit.toString())
 if (params?.search) queryParams.append('search', params.search)

 const queryString = queryParams.toString()
 return this.get<any>(`/benefits${queryString ? `?${queryString}` : ''}`)
 }

 getBenefitById(benefitId: string) {
 return this.get<any>(`/benefits/${benefitId}`)
 }

 createBenefit(data: any) {
 return this.request<any>('/benefits', {
 method: 'POST',
 body: JSON.stringify(data)
 })
 }

 updateBenefit(benefitId: string, data: any) {
 return this.request<any>(`/benefits/${benefitId}`, {
 method: 'PATCH',
 body: JSON.stringify(data)
 })
 }

 deleteBenefit(benefitId: string) {
 return this.delete(`/benefits/${benefitId}`)
 }

 // ===== DEPRECIATION RULES =====

 getDepreciationRules() {
 return this.get<Array<{
 id: string
 label: string
 type: string
 threshold: number | null
 conditionValue: string | null
 percentage: number
 isActive: boolean
 createdAt: string
 updatedAt: string
 }>>('/depreciation-rules')
 }

 createDepreciationRule(data: {
 label: string
 type: string
 threshold?: number
 conditionValue?: string
 percentage: number
 isActive?: boolean
 }) {
 return this.request<any>('/depreciation-rules', {
 method: 'POST',
 body: JSON.stringify(data),
 })
 }

 updateDepreciationRule(id: string, data: {
 label?: string
 type?: string
 threshold?: number
 conditionValue?: string
 percentage?: number
 isActive?: boolean
 }) {
 return this.request<any>(`/depreciation-rules/${id}`, {
 method: 'PATCH',
 body: JSON.stringify(data),
 })
 }

 deleteDepreciationRule(id: string) {
 return this.delete(`/depreciation-rules/${id}`)
 }

 // ===== PROPOSALS CONFIG =====

 // Payment Forms
 getPaymentForms() {
 return this.get<Array<{
 id: string
 name: string
 description?: string
 isFinancing: boolean
 isCashPayment: boolean
 createdAt: string
 updatedAt: string
 }>>('/proposals/payment-forms')
 }

 createPaymentForm(data: {
 name: string
 description?: string
 isFinancing?: boolean
 isCashPayment?: boolean
 }) {
 return this.post<{
 id: string
 name: string
 description?: string
 isFinancing: boolean
 isCashPayment: boolean
 createdAt: string
 updatedAt: string
 }>('/proposals/payment-forms', data)
 }

 updatePaymentForm(id: string, data: {
 name?: string
 description?: string
 isFinancing?: boolean
 isCashPayment?: boolean
 }) {
 return this.patch<{
 id: string
 name: string
 description?: string
 isFinancing: boolean
 isCashPayment: boolean
 updatedAt: string
 }>(`/proposals/payment-forms/${id}`, data)
 }

 deletePaymentForm(id: string) {
 return this.delete(`/proposals/payment-forms/${id}`)
 }

 // Payment Options
 getPaymentOptions(paymentFormId?: string) {
 const endpoint = paymentFormId 
 ? `/proposals/payment-options?paymentFormId=${paymentFormId}`
 : '/proposals/payment-options'
 return this.get<Array<{
 id: string
 paymentFormId: string
 name: string
 description?: string
 installments: number
 interestRate?: number
 createdAt: string
 updatedAt: string
 }>>(endpoint)
 }

 createPaymentOption(data: {
 paymentFormId: string
 name: string
 description?: string
 installments?: number
 interestRate?: number
 }) {
 return this.post<{
 id: string
 paymentFormId: string
 name: string
 description?: string
 installments: number
 interestRate?: number
 createdAt: string
 updatedAt: string
 }>('/proposals/payment-options', data)
 }

 updatePaymentOption(id: string, data: {
 paymentFormId?: string
 name?: string
 description?: string
 installments?: number
 interestRate?: number
 }) {
 return this.patch<{
 id: string
 paymentFormId: string
 name: string
 description?: string
 installments: number
 interestRate?: number
 updatedAt: string
 }>(`/proposals/payment-options/${id}`, data)
 }

 deletePaymentOption(id: string) {
 return this.delete(`/proposals/payment-options/${id}`)
 }

 // Proposal Benefits
 getProposalBenefits() {
 return this.get<Array<{
 id: string
 name: string
 value: number
 createdAt: string
 updatedAt: string
 }>>('/proposals/benefits')
 }

 getProposalBenefit(id: string) {
 return this.get<{
 id: string
 name: string
 value: number
 createdAt: string
 updatedAt: string
 }>(`/proposals/benefits/${id}`)
 }

 createProposalBenefit(data: { name: string; value: number }) {
 return this.post<{
 id: string
 name: string
 value: number
 createdAt: string
 updatedAt: string
 }>('/proposals/benefits', data)
 }

 updateProposalBenefit(id: string, data: { name?: string; value?: number }) {
 return this.patch<{
 id: string
 name: string
 value: number
 updatedAt: string
 }>(`/proposals/benefits/${id}`, data)
 }

 deleteProposalBenefit(id: string) {
 return this.delete(`/proposals/benefits/${id}`)
 }

 // ==================== SALES ====================
 
getSales(params?: { page?: number; limit?: number; operationalStage?: string; status?: string }) {
  const queryParams = new URLSearchParams()
  if (params?.page) queryParams.append('page', params.page.toString())
  if (params?.limit) queryParams.append('limit', params.limit.toString())
  if (params?.operationalStage) queryParams.append('operationalStage', params.operationalStage)
  if (params?.status) queryParams.append('status', params.status)
  const query = queryParams.toString()
  return this.get<any>(`/sales${query ? `?${query}` : ''}`)
}

  getPreparationQueue(params?: { page?: number; limit?: number; status?: string; statuses?: string[] }) {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.status) queryParams.append('status', params.status)
    if (params?.statuses && params.statuses.length > 0) {
      params.statuses.forEach((s) => queryParams.append('statuses', s))
    }
    const query = queryParams.toString()
    return this.get<any>(`/sales/preparation-queue${query ? `?${query}` : ''}`)
  }

  getLeadSources() {
    return this.get<Array<{
      id: string
      key: string
      label: string
      color?: string | null
      isActive: boolean
      sortOrder: number
    }>>('/leads/sources')
  }

  createLeadSource(data: {
    key: string
    label: string
    color?: string
    isActive?: boolean
    sortOrder?: number
  }) {
    return this.request('/leads/sources', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  updateLeadSource(id: string, data: {
    key?: string
    label?: string
    color?: string
    isActive?: boolean
    sortOrder?: number
  }) {
    return this.request(`/leads/sources/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  deleteLeadSource(id: string) {
    return this.delete(`/leads/sources/${id}`)
  }

  getLeadMedia() {
    return this.get<Array<{
      id: string
      key: string
      label: string
      color?: string | null
      isOfficial: boolean
      isActive: boolean
      sortOrder: number
    }>>('/leads/media')
  }

  getLeadMediaCatalog() {
    return this.get<Array<{
      id: string
      key: string
      label: string
      color?: string | null
      tier: 'spotlight' | 'standard'
      sortOrder: number
      isSelected: boolean
    }>>('/leads/media/catalog')
  }

  replaceWorkingLeadMedia(catalogIds: string[]) {
    return this.request<Array<{
      id: string
      key: string
      label: string
      color?: string | null
      tier: 'spotlight' | 'standard'
      sortOrder: number
      isSelected: boolean
    }>>('/leads/media/working', {
      method: 'PUT',
      body: JSON.stringify({ catalogIds }),
    })
  }

  createLeadMedia(data: {
    key: string
    label: string
    color?: string
    isActive?: boolean
    sortOrder?: number
  }) {
    return this.request('/leads/media', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  deleteLeadMedia(id: string) {
    return this.delete(`/leads/media/${id}`)
  }

  getLeadWebhooks() {
    return this.get<Array<{
      id: string
      name: string
      media: string
      targetUrl?: string | null
      secret: string
      isSystem: boolean
      isActive: boolean
      createdAt: string
      updatedAt: string
    }>>('/leads/webhooks')
  }

  createLeadWebhook(data: {
    name: string
    media: string
    targetUrl?: string
    isActive?: boolean
  }) {
    return this.request('/leads/webhooks', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  updateLeadWebhook(id: string, data: {
    name?: string
    media?: string
    targetUrl?: string
    isActive?: boolean
  }) {
    return this.request(`/leads/webhooks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  deleteLeadWebhook(id: string) {
    return this.delete(`/leads/webhooks/${id}`)
  }

  patchPreparationQueueItem(
    queueId: string,
    data: { status?: string; kanbanColumnId?: string; deliveryAt?: string }
  ) {
    return this.request<any>(`/sales/preparation-queue/${queueId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

 patchSaleOperationalStage(saleId: string, operationalStage: string) {
 return this.request<any>(`/sales/${saleId}/operational-stage`, {
 method: 'PATCH',
 body: JSON.stringify({ operationalStage }),
 })
 }

 patchSalePreparationKanban(saleId: string, kanbanColumnId: string, deliveryAt?: string) {
 return this.request<any>(`/sales/${saleId}/preparation-kanban`, {
 method: 'PATCH',
body: JSON.stringify({ kanbanColumnId, ...(deliveryAt ? { deliveryAt } : {}) }),
 })
 }

 getSaleKanbanColumns(operationalStage: string) {
 return this.get<any>(`/sales/kanban-columns?operationalStage=${encodeURIComponent(operationalStage)}`)
 }

 createSaleKanbanColumn(data: {
 operationalStage: string
 title: string
 isActive?: boolean
 isHandoffColumn?: boolean
responsibleUserId?: string | null
 }) {
 return this.request<any>('/sales/kanban-columns', {
 method: 'POST',
 body: JSON.stringify(data),
 })
 }

 updateSaleKanbanColumn(
 columnId: string,
data: { title?: string; isActive?: boolean; isHandoffColumn?: boolean; responsibleUserId?: string | null }
 ) {
 return this.request<any>(`/sales/kanban-columns/${columnId}`, {
 method: 'PATCH',
 body: JSON.stringify(data),
 })
 }

 deleteSaleKanbanColumn(columnId: string) {
 return this.request<void>(`/sales/kanban-columns/${columnId}`, {
 method: 'DELETE',
 })
 }

 getKanbanColumnChecklist(columnId: string) {
 return this.get<{ items: Array<{ id: string; title: string; sortOrder: number; required: boolean }> }>(
 `/sales/kanban-columns/${encodeURIComponent(columnId)}/checklist`
 )
 }

 putKanbanColumnChecklist(
 columnId: string,
 items: Array<{ title: string; sortOrder?: number; required?: boolean }>
 ) {
 return this.request<{ items: Array<{ id: string; title: string; sortOrder: number; required: boolean }> }>(
 `/sales/kanban-columns/${encodeURIComponent(columnId)}/checklist`,
 {
 method: 'PUT',
 body: JSON.stringify({ items }),
 }
 )
 }

 patchSaleOperationalComment(saleId: string, operationalComment: string | null) {
 return this.request<{ id: string; operationalComment: string | null }>(
 `/sales/${encodeURIComponent(saleId)}/operational-comment`,
 {
 method: 'PATCH',
 body: JSON.stringify({ operationalComment }),
 }
 )
 }

 postSaleOperationalComment(saleId: string, data: { body: string }) {
 return this.request<{
  id: string;
  saleId: string;
  authorId: string;
  body: string;
  createdAt: string;
  author: { id: string; name: string; email: string };
 }>(`/sales/${encodeURIComponent(saleId)}/operational-comments`, {
  method: 'POST',
  body: JSON.stringify(data),
 })
 }

 reorderSaleKanbanColumns(operationalStage: string, orderedColumnIds: string[]) {
 return this.request<any>('/sales/kanban-columns/reorder', {
 method: 'POST',
 body: JSON.stringify({ operationalStage, orderedColumnIds }),
 })
 }

 patchSaleChecklistItem(
 saleId: string,
 itemId: string,
 data: { completed?: boolean; notes?: string | null }
 ) {
 return this.request<any>(`/sales/${saleId}/checklist-items/${itemId}`, {
 method: 'PATCH',
 body: JSON.stringify(data),
 })
 }

 patchSaleDelivery(
 saleId: string,
 data: { deliveryAt?: string | null; deliveryResponsibleId?: string | null }
 ) {
 return this.request<any>(`/sales/${saleId}/delivery`, {
 method: 'PATCH',
 body: JSON.stringify(data),
 })
 }

 getSaleChecklistTemplates(params?: { operationalStage?: string }) {
 const q = new URLSearchParams()
 if (params?.operationalStage) q.append('operationalStage', params.operationalStage)
 const qs = q.toString()
 return this.get<any>(`/sales/checklist-templates${qs ? `?${qs}` : ''}`)
 }

 createSaleChecklistTemplate(data: {
 operationalStage: string
 name: string
 isActive?: boolean
 isDefault?: boolean
 items: Array<{ title: string; sortOrder?: number; required?: boolean }>
 }) {
 return this.request<any>('/sales/checklist-templates', {
 method: 'POST',
 body: JSON.stringify(data),
 })
 }

 updateSaleChecklistTemplate(
 templateId: string,
 data: {
 operationalStage?: string
 name?: string
 isActive?: boolean
 isDefault?: boolean
 items?: Array<{ title: string; sortOrder?: number; required?: boolean }>
 }
 ) {
 return this.request<any>(`/sales/checklist-templates/${templateId}`, {
 method: 'PATCH',
 body: JSON.stringify(data),
 })
 }

 deleteSaleChecklistTemplate(templateId: string) {
 return this.request<void>(`/sales/checklist-templates/${templateId}`, {
 method: 'DELETE',
 })
 }

 getSaleById(saleId: string) {
 return this.get<any>(`/sales/${saleId}`)
 }

  getSalePreparationDetail(saleId: string) {
    const path = `/sales/${encodeURIComponent(saleId)}/preparation-detail`
    return this.get<unknown>(path).then((raw) => this.normalizePreparationDetailPayload(raw, saleId))
  }

  private normalizePreparationDetailPayload(raw: unknown, routeSaleId: string): SalePreparationDetail {
    let cur: unknown = raw
    for (let i = 0; i < 6; i++) {
      if (cur !== null && typeof cur === 'object') {
        const o = cur as Record<string, unknown>
        if (typeof o.saleId === 'string' && o.saleId.length > 0) {
          return this.ensurePreparationDetailArrays(cur as SalePreparationDetail)
        }
      }
      const unwrapped = this.unwrapApiData<unknown>(cur)
      if (unwrapped !== cur) {
        cur = unwrapped
        continue
      }
      if (cur !== null && typeof cur === 'object' && 'data' in cur) {
        const inner = (cur as { data: unknown }).data
        if (inner !== undefined && inner !== cur) {
          cur = inner
          continue
        }
      }
      break
    }
    if (cur === null || typeof cur !== 'object') {
      throw new ApiError({ message: 'Invalid preparation detail response', status: 502 })
    }
    const o = cur as Record<string, unknown>
    const merged: Record<string, unknown> = { ...o }
    merged.saleId =
      typeof o.saleId === 'string' && o.saleId.length > 0 ? o.saleId : routeSaleId
    if (!Array.isArray(merged.preparationChecklist)) merged.preparationChecklist = []
    if (!Array.isArray(merged.preparationTasks)) merged.preparationTasks = []
    if (
      merged.operationalProgress === null ||
      typeof merged.operationalProgress !== 'object' ||
      Array.isArray(merged.operationalProgress)
    ) {
      merged.operationalProgress = { done: 0, total: 0 }
    } else {
      const p = merged.operationalProgress as Record<string, unknown>
      merged.operationalProgress = {
        done: Number(p.done) || 0,
        total: Number(p.total) || 0,
      }
    }
    return this.ensurePreparationDetailArrays({
      saleId: typeof merged.saleId === 'string' ? merged.saleId : routeSaleId,
      status: typeof merged.status === 'string' ? merged.status as Sale['status'] : 'open',
      operationalStage: typeof merged.operationalStage === 'string' ? merged.operationalStage as SaleOperationalStage : undefined,
      operationalKanbanColumn: merged.operationalKanbanColumn as SaleOperationalKanbanColumn | null | undefined,
      operationalKanbanColumnEnteredAt: typeof merged.operationalKanbanColumnEnteredAt === 'string' ? merged.operationalKanbanColumnEnteredAt : null,
      operationalComment: typeof merged.operationalComment === 'string' ? merged.operationalComment : null,
      operationalActivity: merged.operationalActivity as { events: SaleOperationalActivityEvent[] } | undefined,
      deliveryAt: typeof merged.deliveryAt === 'string' ? merged.deliveryAt : null,
      deliveryResponsibleId: typeof merged.deliveryResponsibleId === 'string' ? merged.deliveryResponsibleId : null,
      vehicle: merged.vehicle as Vehicle | undefined,
      customer: merged.customer as Pick<Customer, 'id' | 'name' | 'document' | 'phone'> | undefined,
      preparationChecklist: Array.isArray(merged.preparationChecklist) ? merged.preparationChecklist : [],
      preparationTasks: Array.isArray(merged.preparationTasks) ? merged.preparationTasks : [],
      operationalProgress: merged.operationalProgress as { done: number; total: number },
      documents: Array.isArray(merged.documents) ? merged.documents : []
    })
  }

  private ensurePreparationDetailArrays(d: SalePreparationDetail): SalePreparationDetail {
    return {
      ...d,
      preparationChecklist: Array.isArray(d.preparationChecklist) ? d.preparationChecklist : [],
      preparationTasks: Array.isArray(d.preparationTasks) ? d.preparationTasks : [],
      operationalProgress:
        d.operationalProgress &&
        typeof d.operationalProgress === 'object' &&
        'done' in d.operationalProgress &&
        'total' in d.operationalProgress
          ? {
              done: Number(d.operationalProgress.done) || 0,
              total: Number(d.operationalProgress.total) || 0,
            }
          : { done: 0, total: 0 },
    }
  }

  getPreparationAssignees() {
    return this.get<unknown>('/sales/preparation-assignees').then((raw) =>
      this.unwrapApiData<Array<{ id: string; name: string; email: string }>>(raw),
    )
  }

 getSaleFiscalExportReadiness(saleId: string) {
 return this.get<FiscalExportReadinessResponse>(`/sales/${saleId}/fiscal-export/readiness`)
 }

 getSaleFiscalExport(saleId: string) {
 return this.get<FiscalExportPayload>(`/sales/${saleId}/fiscal-export`)
 }

 getSaleFiscalExportPdfBlob(saleId: string) {
 return this.getBlob(`/sales/${saleId}/fiscal-export/pdf`)
 }

 getCustomerById(customerId: string) {
 return this.get<CustomerRecord>(`/customers/${customerId}`)
 }

 updateCustomer(
 customerId: string,
 data: Partial<{
 documentType: 'cpf' | 'cnpj'
 document: string
 name: string
 email: string
 phone: string
 address: string
 notes: string
 primaryAddress: {
 street: string
 number: string
 complement?: string
 neighborhood: string
 city: string
 state: string
 zipCode: string
 }
 }>
 ) {
 return this.request<CustomerRecord>(`/customers/${customerId}`, {
 method: 'PATCH',
 body: JSON.stringify(data),
 })
 }

 getSaleOnboardingProgress(saleId: string) {
 return this.get<SaleOnboardingProgress>(`/sales/${saleId}/onboarding-progress`)
 }

 getSaleDocumentationChecklist(saleId: string) {
 return this.get<SaleDocumentationChecklistRow[]>(`/sales/${saleId}/documentation-checklist`)
 }

 patchSaleDocumentationChecklist(saleId: string, checklistKey: string, body: { completed: boolean }) {
 return this.request<SaleDocumentationChecklistRow>(`/sales/${saleId}/documentation-checklist/${checklistKey}`, {
 method: 'PATCH',
 body: JSON.stringify(body),
 })
 }

 sendSaleToPreparationPipeline(saleId: string) {
 return this.request<any>(`/sales/${saleId}/send-to-preparation-pipeline`, {
 method: 'POST',
 body: JSON.stringify({}),
 })
 }

 patchSaleCustomerDocFulfillment(saleId: string, fulfillmentId: string, entityDocumentId: string) {
 return this.request<any>(`/sales/${saleId}/onboarding/customer-documents/${fulfillmentId}`, {
 method: 'PATCH',
 body: JSON.stringify({ entityDocumentId }),
 })
 }

 patchSaleContractFulfillment(
 saleId: string,
 fulfillmentId: string,
 data: { signedAt: string; entityDocumentId?: string }
 ) {
 return this.request<any>(`/sales/${saleId}/onboarding/contracts/${fulfillmentId}`, {
 method: 'PATCH',
 body: JSON.stringify(data),
 })
 }

 getSaleCustomerDocumentTypes() {
 return this.get<SaleCustomerDocumentType[]>('/sales/sale-customer-document-types')
 }

 createSaleCustomerDocumentType(data: {
 name: string
 slug: string
 sortOrder?: number
 isRequired?: boolean
 isActive?: boolean
 attachTo?: 'sale' | 'customer'
 }) {
 return this.request<SaleCustomerDocumentType>('/sales/sale-customer-document-types', {
 method: 'POST',
 body: JSON.stringify(data),
 })
 }

 updateSaleCustomerDocumentType(
 typeId: string,
 data: {
 name?: string
 slug?: string
 sortOrder?: number
 isRequired?: boolean
 isActive?: boolean
 attachTo?: 'sale' | 'customer'
 }
 ) {
 return this.request<SaleCustomerDocumentType>(`/sales/sale-customer-document-types/${typeId}`, {
 method: 'PATCH',
 body: JSON.stringify(data),
 })
 }

 deleteSaleCustomerDocumentType(typeId: string) {
 return this.request<void>(`/sales/sale-customer-document-types/${typeId}`, {
 method: 'DELETE',
 })
 }

 getSaleContractTemplates() {
 return this.get<SaleContractTemplate[]>('/sales/sale-contract-templates')
 }

 uploadSaleContractTemplateReference(formData: FormData) {
 return this.uploadFormData<unknown>('/sales/sale-contract-templates/upload-reference', formData).then(
 (raw) => this.unwrapApiData<{ filename: string; url: string }>(raw),
 )
 }

 createSaleContractTemplate(data: {
 name: string
 sortOrder?: number
 isRequired?: boolean
 isActive?: boolean
 referenceFileUrl?: string
 referenceTempFilename?: string
 }) {
 return this.request<SaleContractTemplate>('/sales/sale-contract-templates', {
 method: 'POST',
 body: JSON.stringify(data),
 })
 }

 updateSaleContractTemplate(
 templateId: string,
 data: {
 name?: string
 sortOrder?: number
 isRequired?: boolean
 isActive?: boolean
 referenceFileUrl?: string | null
 referenceTempFilename?: string
 }
 ) {
 return this.request<SaleContractTemplate>(`/sales/sale-contract-templates/${templateId}`, {
 method: 'PATCH',
 body: JSON.stringify(data),
 })
 }

 deleteSaleContractTemplate(templateId: string) {
 return this.request<void>(`/sales/sale-contract-templates/${templateId}`, {
 method: 'DELETE',
 })
 }

 updateSale(saleId: string, data: any) {
 return this.request<any>(`/sales/${saleId}`, {
 method: 'PATCH',
 body: JSON.stringify(data)
 })
 }

 getSaleTimeline(saleId: string) {
 return this.get<{ events: Array<{ occurredAt: string; type: string; title: string; payload: Record<string, unknown> }> }>(
 `/sales/${saleId}/timeline`
 )
 }

 updateSaleStages(
 saleId: string,
 data: { administrativeStage?: string; preparationStage?: string }
 ) {
 return this.request<any>(`/sales/${saleId}/stages`, {
 method: 'PATCH',
 body: JSON.stringify(data),
 })
 }

 createSalePreparationTask(
 saleId: string,
 data: {
 stepKey: string;
 status?: string;
 notes?: string;
 assigneeId?: string;
 saleCostId?: string;
 completedAt?: string;
 }
 ) {
 return this.request<any>(`/sales/${saleId}/preparation-tasks`, {
 method: 'POST',
 body: JSON.stringify(data),
 })
 }

 updateSalePreparationTask(
 saleId: string,
 taskId: string,
 data: {
 status?: string;
 notes?: string;
 assigneeId?: string;
 saleCostId?: string | null;
 completedAt?: string | null;
 }
 ) {
 return this.request<any>(`/sales/${saleId}/preparation-tasks/${taskId}`, {
 method: 'PATCH',
 body: JSON.stringify(data),
 })
 }

  bulkAssignSalePreparationTasks(
    saleId: string,
    data: {
      assigneeId: string
      onlyUnassigned?: boolean
    }
  ) {
    return this.request<any>(`/sales/${saleId}/preparation-tasks/assignee`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

 // ==================== INVOICES ====================
 
 getInvoices(params?: { page?: number; limit?: number }) {
 const queryParams = new URLSearchParams()
 if (params?.page) queryParams.append('page', params.page.toString())
 if (params?.limit) queryParams.append('limit', params.limit.toString())
 const query = queryParams.toString()
 return this.get<any>(`/invoices${query ? `?${query}` : ''}`)
 }

 getInvoiceById(invoiceId: string) {
 return this.get<any>(`/invoices/${invoiceId}`)
 }

 updateInvoice(invoiceId: string, data: any) {
 return this.request<any>(`/invoices/${invoiceId}`, {
 method: 'PATCH',
 body: JSON.stringify(data)
 })
 }

 // ==================== CLOSURES ====================
 
 createClosure(data: { startDate: string; endDate: string }) {
 return this.request<any>('/closures', {
 method: 'POST',
 body: JSON.stringify(data)
 })
 }

 getClosures(params?: { page?: number; limit?: number }) {
 const queryParams = new URLSearchParams()
 if (params?.page) queryParams.append('page', params.page.toString())
 if (params?.limit) queryParams.append('limit', params.limit.toString())
 const query = queryParams.toString()
 return this.get<any>(`/closures${query ? `?${query}` : ''}`)
 }

 getClosureById(closureId: string) {
 return this.get<any>(`/closures/${closureId}`)
 }

 getClosureSummary(closureId: string) {
 return this.get<any>(`/closures/${closureId}/summary`)
 }

 addSalesToClosure(closureId: string, data: { saleIds: string[] }) {
 return this.request<any>(`/closures/${closureId}/sales`, {
 method: 'POST',
 body: JSON.stringify(data)
 })
 }

 closeClosure(closureId: string) {
 return this.request<any>(`/closures/${closureId}/close`, {
 method: 'PATCH',
 body: JSON.stringify({})
 })
 }

 // ==================== COMMISSION RULES ====================
 
 createCommissionRule(data: any) {
 return this.request<any>('/commission-rules', {
 method: 'POST',
 body: JSON.stringify(data)
 })
 }

 getCommissionRules() {
 return this.get<any>('/commission-rules')
 }

 getCommissionRuleById(ruleId: string) {
 return this.get<any>(`/commission-rules/${ruleId}`)
 }

 updateCommissionRule(ruleId: string, data: any) {
 return this.request<any>(`/commission-rules/${ruleId}`, {
 method: 'PATCH',
 body: JSON.stringify(data)
 })
 }

 duplicateCommissionRule(ruleId: string) {
 return this.request<any>(`/commission-rules/${ruleId}/duplicate`, {
 method: 'POST'
 })
 }

 deleteCommissionRule(ruleId: string) {
 return this.delete(`/commission-rules/${ruleId}`)
 }

 getDealershipMe() {
 return this.get<DealershipProfileMe>('/dealerships/me')
 }

 patchDealershipMe(data: { commissionApplyStoreRulesToAllSales?: boolean }) {
 return this.patch<DealershipProfileMe>('/dealerships/me', data)
 }

 // ==================== COMMISSIONS ====================
 
 createCommission(data: { saleId: string; ruleId: string; salespersonId?: string }) {
 return this.request<any>('/commissions', {
 method: 'POST',
 body: JSON.stringify(data)
 })
 }

 getCommissions(params?: { page?: number; limit?: number }) {
 const queryParams = new URLSearchParams()
 if (params?.page) queryParams.append('page', params.page.toString())
 if (params?.limit) queryParams.append('limit', params.limit.toString())
 const query = queryParams.toString()
 return this.get<any>(`/commissions${query ? `?${query}` : ''}`)
 }

 getCommissionById(commissionId: string) {
 return this.get<any>(`/commissions/${commissionId}`)
 }

 getCommissionsBySalesperson(salespersonId: string, params?: { page?: number; limit?: number }) {
 const queryParams = new URLSearchParams()
 if (params?.page) queryParams.append('page', params.page.toString())
 if (params?.limit) queryParams.append('limit', params.limit.toString())
 const query = queryParams.toString()
 return this.get<any>(`/commissions/by-salesperson/${salespersonId}${query ? `?${query}` : ''}`)
 }

 markCommissionAsPaid(commissionId: string, paymentId?: string) {
 const body =
 paymentId && paymentId.length > 0 ? JSON.stringify({ paymentId }) : JSON.stringify({})
 return this.request<any>(`/commissions/${commissionId}/pay`, {
 method: 'PATCH',
 body,
 })
 }

 updateCommission(
 commissionId: string,
 data: { baseValue?: number; percentage?: number; calculatedValue?: number }
 ) {
 return this.request<any>(`/commissions/${commissionId}`, {
 method: 'PATCH',
 body: JSON.stringify(data),
 })
 }

 createCommissionBonus(commissionId: string, data: { label: string; amount: number }) {
 return this.post<{
 id: string
 commissionId: string
 label: string
 amount: number
 sortOrder: number
 createdAt: string
 updatedAt: string
 }>(`/commissions/${commissionId}/bonuses`, data)
 }

 updateCommissionBonus(
 commissionId: string,
 bonusId: string,
 data: { label?: string; amount?: number }
 ) {
 return this.request<{
 id: string
 commissionId: string
 label: string
 amount: number
 sortOrder: number
 createdAt: string
 updatedAt: string
 }>(`/commissions/${commissionId}/bonuses/${bonusId}`, {
 method: 'PATCH',
 body: JSON.stringify(data),
 })
 }

 deleteCommissionBonus(commissionId: string, bonusId: string) {
 return this.delete(`/commissions/${commissionId}/bonuses/${bonusId}`)
 }

 deleteCommission(commissionId: string) {
 return this.delete(`/commissions/${commissionId}`)
 }

 confirmSaleCommissionsOnboardingStep(saleId: string) {
 return this.request<unknown>(`/sales/${saleId}/onboarding/commissions/confirm`, {
 method: 'POST',
 body: JSON.stringify({}),
 })
 }

 // ==================== PAYMENTS ====================
 
 createPayment(data: {
 invoiceId: string;
 amount: number;
 paymentDate: string;
 paymentMethod: string; // PaymentForm ID
 status?: 'pending' | 'paid' | 'cancelled';
 reference?: string;
 notes?: string;
 }) {
 return this.request<any>('/payments', {
 method: 'POST',
 body: JSON.stringify(data)
 })
 }

 getPayments(params?: { page?: number; limit?: number }) {
 const queryParams = new URLSearchParams()
 if (params?.page) queryParams.append('page', params.page.toString())
 if (params?.limit) queryParams.append('limit', params.limit.toString())
 const query = queryParams.toString()
 return this.get<any>(`/payments${query ? `?${query}` : ''}`)
 }

 getPaymentById(paymentId: string) {
 return this.get<any>(`/payments/${paymentId}`)
 }

 updatePayment(paymentId: string, data: any) {
 return this.request<any>(`/payments/${paymentId}`, {
 method: 'PATCH',
 body: JSON.stringify(data)
 })
 }

 deletePayment(paymentId: string) {
 return this.delete(`/payments/${paymentId}`)
 }

 // Entity Documents
 uploadEntityDocumentTemp(formData: FormData) {
 return this.uploadFormData<unknown>('/entity-documents/upload-temp', formData).then((raw) =>
 this.unwrapApiData<Record<string, unknown>>(raw),
 )
 }

 createEntityDocument(data: {
 entityType: string
 entityId: string
 name: string
 url?: string
 type?: string
 expiryDate?: string
 tempFilename?: string
 saleId?: string
 }) {
 return this.request<unknown>('/entity-documents', {
 method: 'POST',
 body: JSON.stringify(data),
 }).then((raw) => this.unwrapApiData<unknown>(raw))
 }

 async getEntityDocuments(entityType: string, entityId: string) {
 const raw = await this.get<unknown>(`/entity-documents?entityType=${entityType}&entityId=${entityId}`)
 const direct = this.coerceArrayResponse(raw)
 if (direct.length > 0 || Array.isArray(raw)) {
 return direct
 }
 return this.coerceArrayResponse(this.unwrapApiData<unknown>(raw))
 }

 deleteEntityDocument(documentId: string) {
 return this.delete(`/entity-documents/${documentId}`)
 }

 // Fiscal Documents
 getFiscalDocuments(params?: {
 saleId?: string
 status?: string
 page?: number
 limit?: number
 }) {
 const queryParams = new URLSearchParams()
 if (params?.saleId) queryParams.append('saleId', params.saleId)
 if (params?.status) queryParams.append('status', params.status)
 if (params?.page) queryParams.append('page', params.page.toString())
 if (params?.limit) queryParams.append('limit', params.limit.toString())
 const query = queryParams.toString()
 return this.get<any>(`/fiscal-documents${query ? `?${query}` : ''}`)
 }

 getFiscalDocumentById(documentId: string) {
 return this.get<any>(`/fiscal-documents/${documentId}`)
 }

 getFiscalDocumentsBySale(saleId: string) {
 return this.get<any>(`/fiscal-documents/sale/${saleId}`)
 }

 downloadFiscalDocumentXml(documentId: string) {
 return fetch(`${this.baseUrl}/fiscal-documents/${documentId}/xml`, {
 method: 'GET',
 headers: {
 'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
 },
 }).then((response) => {
 if (!response.ok) throw new Error('Failed to download XML')
 return response.blob()
 })
 }

 // Vehicle Documents
 async getVehicleDocuments(vehicleId: string) {
 const raw = await this.get<unknown>(`/vehicles/${vehicleId}/documents`)
 const normalized = normalizeVehicleDocumentsPayload(raw)
 if (normalized.length > 0) {
 return normalized
 }
 const unwrapped = this.unwrapApiData<unknown>(raw)
 const fromUnwrap = normalizeVehicleDocumentsPayload(unwrapped)
 if (fromUnwrap.length > 0) {
 return fromUnwrap
 }
 const direct = this.coerceArrayResponse(raw)
 if (direct.length > 0 || Array.isArray(raw)) {
 return direct
 }
 return this.coerceArrayResponse(unwrapped)
 }

 deleteVehicleDocument(vehicleId: string, documentId: string) {
 return this.delete(`/vehicles/${vehicleId}/documents/${documentId}`)
 }

 // ==================== DASHBOARD ====================

 getDashboardMetrics() {
 return this.get<DashboardMetrics>('/dashboard/metrics')
 }

 getDashboardSalesChart() {
 return this.get<MonthlySalesData[]>('/dashboard/sales-chart')
 }

 getDashboardLeadsFunnel() {
 return this.get<LeadStatusData[]>('/dashboard/leads-funnel')
 }

 getDashboardRecentActivities() {
 return this.get<RecentLeadData[]>('/dashboard/recent-activities')
 }

 getDashboardOverview() {
 return this.get<DashboardOverview>('/dashboard/overview')
 }

 // ==================== FINANCIAL DASHBOARD ====================

 getFinancialOverview(startDate?: string, endDate?: string) {
 const params = new URLSearchParams()
 if (startDate) params.append('startDate', startDate)
 if (endDate) params.append('endDate', endDate)
 const query = params.toString() ? `?${params.toString()}` : ''
 return this.get<FinancialOverview>(`/financial/dashboard/overview${query}`)
 }

 getFinancialCashFlow(months = 6) {
 return this.get<CashFlowData[]>(`/financial/dashboard/cash-flow?months=${months}`)
 }

 getFinancialKPIs(startDate?: string, endDate?: string) {
 const params = new URLSearchParams()
 if (startDate) params.append('startDate', startDate)
 if (endDate) params.append('endDate', endDate)
 const query = params.toString() ? `?${params.toString()}` : ''
 return this.get<FinancialKPIs>(`/financial/dashboard/kpis${query}`)
 }

 getFinancialPending() {
 return this.get<PendingFinancials>('/financial/dashboard/pending')
 }

 // ==================== POST-SALES DASHBOARD ====================

 getPostSalesMetrics() {
 return this.get<PostSalesDashboardMetrics>('/post-sales/dashboard/metrics')
 }

 getPostSalesTimeline(months = 6) {
 return this.get<DeliveryTimeline[]>(`/post-sales/dashboard/timeline?months=${months}`)
 }

 getPostSalesNPSDistribution() {
 return this.get<NPSDistribution[]>('/post-sales/dashboard/nps-distribution')
 }

 getPostSalesRecentDeliveries(limit = 10) {
 return this.get<RecentDelivery[]>(`/post-sales/dashboard/recent-deliveries?limit=${limit}`)
 }

 // ==================== INTEGRAÇÕES ====================

 getIntegrations() {
 return this.get<MarketplaceIntegration[]>('/integrations')
 }

 createIntegration(data: {
 platform: string
 apiKey?: string
 apiSecret?: string
 accessToken?: string
 isActive?: boolean
 autoPublish?: boolean
 }) {
 return this.post<MarketplaceIntegration>('/integrations', data)
 }

 updateIntegration(
 id: string,
 data: {
 apiKey?: string
 apiSecret?: string
 accessToken?: string
 isActive?: boolean
 autoPublish?: boolean
 },
 ) {
 return this.patch<MarketplaceIntegration>(`/integrations/${id}`, data)
 }

 deleteIntegration(id: string) {
 return this.delete(`/integrations/${id}`)
 }

 testIntegration(id: string) {
 return this.post<{ success: boolean; error?: string }>(`/integrations/${id}/test`, {})
 }

 // Publicações
 publishVehicle(data: { vehicleId: string; platform: string }) {
 return this.post<VehiclePublication>('/integrations/publish', data)
 }

 unpublishVehicle(publicationId: string) {
 return this.delete(`/integrations/publications/${publicationId}`)
 }

 getVehiclePublications(vehicleId?: string) {
 const endpoint = vehicleId
 ? `/integrations/vehicles/${vehicleId}/publications`
 : '/integrations/publications'
 return this.get<VehiclePublication[]>(endpoint)
 }

 // ==================== PRODUTOS/SERVIÇOS ====================

 getProducts(filters?: {
 type?: 'product' | 'service'
 category?: string
 isActive?: boolean
 search?: string
 }) {
 const params = new URLSearchParams()
 if (filters?.type) params.append('type', filters.type)
 if (filters?.category) params.append('category', filters.category)
 if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive))
 if (filters?.search) params.append('search', filters.search)
 const query = params.toString() ? `?${params.toString()}` : ''
 return this.get<Product[]>(`/products${query}`)
 }

 getProductStats() {
 return this.get<ProductStats>('/products/stats')
 }

 getProduct(id: string) {
 return this.get<Product>(`/products/${id}`)
 }

 createProduct(data: {
 name: string
 description?: string
 type: 'product' | 'service'
 category: string
 price: number
 cost?: number
 sku?: string
 isActive?: boolean
 supplier?: string
 notes?: string
 }) {
 return this.post<Product>('/products', data)
 }

 updateProduct(
 id: string,
 data: {
 name?: string
 description?: string
 type?: 'product' | 'service'
 category?: string
 price?: number
 cost?: number
 sku?: string
 isActive?: boolean
 supplier?: string
 notes?: string
 },
 ) {
 return this.patch<Product>(`/products/${id}`, data)
 }

 deleteProduct(id: string) {
 return this.delete(`/products/${id}`)
 }

 // ==================== MANUTENÇÃO ====================

 getMaintenanceSummary() {
 return this.get<MaintenanceSummary>('/maintenance/summary')
 }

 getMaintenanceByVehicle(page = 1, limit = 50) {
 return this.get<{ data: MaintenanceRecord[]; total: number }>(
 `/maintenance/vehicles?page=${page}&limit=${limit}`,
 )
 }

 getMaintenanceForVehicle(vehicleId: string, page = 1, limit = 50) {
 return this.get<{ data: MaintenanceRecord[]; total: number }>(
 `/maintenance/vehicles/${vehicleId}?page=${page}&limit=${limit}`,
 )
 }

 getMaintenanceRecord(id: string) {
 return this.get<MaintenanceRecord>(`/maintenance/${id}`)
 }

 createMaintenance(vehicleId: string, data: {
 date: string
 type: 'preventive' | 'corrective' | 'inspection' | 'recall'
 description: string
 cost?: number
 provider?: string
 }) {
 return this.post<MaintenanceRecord>(`/maintenance/vehicles/${vehicleId}`, data)
 }

 updateMaintenance(
 id: string,
 data: {
 date?: string
 type?: 'preventive' | 'corrective' | 'inspection' | 'recall'
 description?: string
 cost?: number
 provider?: string
 },
 ) {
 return this.patch<MaintenanceRecord>(`/maintenance/${id}`, data)
 }

 deleteMaintenance(id: string) {
 return this.delete(`/maintenance/${id}`)
 }

 // ==================== ACCESS CONTROL ====================

 getMe() {
 return this.get<{
 user: {
 id: string
 email: string
 name: string
 context: 'backoffice' | 'dealership'
 dealershipId?: string
 backofficeRole?: string
 dealershipRole?: string
 }
 }>('/auth/me')
 }

 getMyPermissions() {
 return this.get<{ permissions: string[] }>('/auth/me/permissions')
 }

 getDealershipAccessMatrix() {
 return this.get<DealershipAccessMatrix>('/dealerships/me/access')
 }

 updateDealershipAccess(data: {
 roles: Array<{ role: string; permissionKeys: string[] }>
 }) {
 return this.put<DealershipAccessMatrix>('/dealerships/me/access', data)
 }

 // ==================== RBAC ====================

 getRoles(context?: 'backoffice' | 'dealership') {
 const query = context ? `?context=${context}` : ''
 return this.get<Role[]>(`/rbac/roles${query}`)
 }

 getRole(id: string) {
 return this.get<Role>(`/rbac/roles/${id}`)
 }

 createRole(data: {
 name: string
 description?: string
 context: 'backoffice' | 'dealership'
 isSystem?: boolean
 permissionIds?: string[]
 }) {
 return this.post<Role>('/rbac/roles', data)
 }

 updateRole(
 id: string,
 data: {
 name?: string
 description?: string
 isActive?: boolean
 permissionIds?: string[]
 },
 ) {
 return this.patch<Role>(`/rbac/roles/${id}`, data)
 }

 deleteRole(id: string) {
 return this.delete(`/rbac/roles/${id}`)
 }

 getPermissions(resource?: string) {
 const query = resource ? `?resource=${resource}` : ''
 return this.get<Permission[]>(`/rbac/permissions${query}`)
 }

 createPermission(data: {
 name: string
 description?: string
 resource: string
 action: string
 }) {
 return this.post<Permission>('/rbac/permissions', data)
 }

 deletePermission(id: string) {
 return this.delete(`/rbac/permissions/${id}`)
 }

 // ============================================================
 // WHATSAPP METHODS
 // ============================================================

 getWhatsAppConfig() {
 return this.get<WhatsAppConfig>('/whatsapp/config')
 }

 createWhatsAppConfig(data: {
 phoneNumberId: string
 businessAccountId: string
 accessToken: string
 webhookVerifyToken: string
 isActive?: boolean
 autoReply?: boolean
 autoCreateLead?: boolean
 }) {
 return this.post<WhatsAppConfig>('/whatsapp/config', data)
 }

 updateWhatsAppConfig(data: {
 phoneNumberId?: string
 businessAccountId?: string
 accessToken?: string
 webhookVerifyToken?: string
 isActive?: boolean
 autoReply?: boolean
 autoCreateLead?: boolean
 }) {
 return this.patch<WhatsAppConfig>('/whatsapp/config', data)
 }

 deleteWhatsAppConfig() {
 return this.delete('/whatsapp/config')
 }

 testWhatsAppConnection() {
 return this.post<{ success: boolean; error?: string }>('/whatsapp/test', {})
 }

 sendWhatsAppTextMessage(data: { phoneNumberId: string; to: string; message: string; leadId?: string }) {
 return this.post<WhatsAppMessage>('/whatsapp/messages/text', {
 phoneNumberId: data.phoneNumberId,
 to: data.to,
 body: data.message,
 ...(data.leadId !== undefined ? { leadId: data.leadId } : {}),
 })
 }

 sendWhatsAppTemplateMessage(data: {
 to: string
 templateName: string
 languageCode: string
 parameters?: string[]
 leadId?: string
 }) {
 return this.post<WhatsAppMessage>('/whatsapp/messages/template', data)
 }

 sendWhatsAppMediaMessage(data: {
 phoneNumberId: string
 to: string
 mediaType: 'image' | 'video' | 'document'
 mediaUrl: string
 caption?: string
 filename?: string
 leadId?: string
 }) {
 return this.post<WhatsAppMessage>('/whatsapp/messages/media', data)
 }

 async sendWhatsAppMedia(data: {
 phoneNumberId: string
 to: string
 type: 'image' | 'video' | 'document' | 'audio'
 mediaUrl: string | File
 caption?: string
 leadId?: string
 }) {
 // If mediaUrl is a File, we need to upload it first
 if (data.mediaUrl instanceof File) {
 const formData = new FormData()
 formData.append('phoneNumberId', data.phoneNumberId)
 formData.append('to', data.to)
 formData.append('type', data.type)
 formData.append('file', data.mediaUrl)
 if (data.caption) formData.append('caption', data.caption)
 if (data.leadId) formData.append('leadId', data.leadId)

 return this.post<WhatsAppMessage>('/whatsapp/messages/media', formData, true)
 } else {
 // mediaUrl is a string
 return this.post<WhatsAppMessage>('/whatsapp/messages/media', {
 phoneNumberId: data.phoneNumberId,
 to: data.to,
 mediaType: data.type,
 mediaUrl: data.mediaUrl,
 caption: data.caption,
 leadId: data.leadId,
 })
 }
 }

 getWhatsAppMessages(params?: { leadId?: string; phoneNumber?: string; page?: number; limit?: number }) {
 const query = new URLSearchParams()
 if (params?.leadId) query.append('leadId', params.leadId)
 if (params?.phoneNumber) query.append('phoneNumber', params.phoneNumber)
 if (params?.page) query.append('page', params.page.toString())
 if (params?.limit) query.append('limit', params.limit.toString())
 return this.get<{ messages: WhatsAppMessage[]; pagination: any }>(`/whatsapp/messages?${query}`)
 }

 getWhatsAppConversation(phoneNumber: string, page?: number, limit?: number) {
 const encodedPhone = encodeURIComponent(phoneNumber)
 const params = new URLSearchParams()
 if (page !== undefined) params.append('page', page.toString())
 if (limit !== undefined) params.append('limit', limit.toString())
 const qs = params.toString()
 const suffix = qs ? `?${qs}` : ''
 return this.get<{
 messages: WhatsAppMessage[]
 pagination: unknown
 phoneNumberId?: string
 channelSession?: WhatsAppChannelSession
 }>(`/whatsapp/conversations/${encodedPhone}${suffix}`)
 }

 finalizeWhatsAppConversation(phoneNumber: string, phoneNumberId: string) {
 const encodedPhone = encodeURIComponent(phoneNumber)
 return this.post<void>(`/whatsapp/conversations/${encodedPhone}/finalize`, { phoneNumberId })
 }

 reopenWhatsAppConversation(phoneNumber: string, phoneNumberId: string) {
 const encodedPhone = encodeURIComponent(phoneNumber)
 return this.post<void>(`/whatsapp/conversations/${encodedPhone}/reopen`, { phoneNumberId })
 }

  markWhatsAppConversationRead(phoneNumber: string, phoneNumberId: string) {
    const encodedPhone = encodeURIComponent(phoneNumber)
    return this.post<{ markedCount: number; metaReadSent: boolean }>(
      `/whatsapp/conversations/${encodedPhone}/read`,
      { phoneNumberId },
    )
  }

  getWhatsAppConversations(params?: {
    page?: number;
    limit?: number;
    search?: string;
    leadId?: string;
    phoneNumberId?: string;
  }) {
    const page = params?.page ?? 1
    const limit = params?.limit ?? 20
    const query = new URLSearchParams()
    query.set('page', String(page))
    query.set('limit', String(limit))
    if (params?.search?.trim()) query.set('search', params.search.trim())
    if (params?.leadId) query.set('leadId', params.leadId)
    if (params?.phoneNumberId) query.set('phoneNumberId', params.phoneNumberId)
    return this.get<{
      conversations: Array<{
        phoneNumber: string;
        lastMessage?: string;
        lastMessageContent?: string;
        lastMessageAt: string;
        lastMessageType: string;
        lastMessageDirection: string;
        unreadCount: number;
        lead: {
          id: string;
          name: string;
          email?: string;
          status: string;
          assignedTo?: string | null;
        } | null;
      }>;
      pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
      };
    }>(`/whatsapp/conversations?${query.toString()}`)
  }

  getWhatsAppConversationMetrics(phoneNumberId?: string) {
    const query = phoneNumberId ? `?phoneNumberId=${phoneNumberId}` : ''
    return this.get<{
      averageResponseTimeMinutes: number | null
      responseRatePercent: number
      conversionRatePercent: number
    }>(`/whatsapp/conversations/metrics${query}`)
  }

  getWhatsAppConfigs() {
 return this.get<Array<{
   id: string;
   phoneNumberId: string;
   businessAccountId: string;
   displayPhone: string;
   accountName: string | null;
   isPrimary: boolean;
   isActive: boolean;
   createdAt: string;
 }>>('/whatsapp/configs')
 }

  getWhatsAppTemplates(params?: { configId?: string; phoneNumberId?: string }) {
 const query = new URLSearchParams()
 if (params?.configId) query.set('configId', params.configId)
 if (params?.phoneNumberId) query.set('phoneNumberId', params.phoneNumberId)
 const url = query.toString() ? `/whatsapp/templates?${query.toString()}` : '/whatsapp/templates'
 return this.get<{
   accounts?: Array<{
     account: {
       id: string;
       phoneNumberId: string;
       businessAccountId: string;
       displayPhone: string;
       accountName: string | null;
       isPrimary: boolean;
       isActive: boolean;
     };
     templates: WhatsAppTemplate[];
     stats: {
       total: number;
       approved: number;
       pending: number;
       rejected: number;
       totalUsage: number;
     };
   }>;
   account?: {
     id: string;
     phoneNumberId: string;
     businessAccountId: string;
     displayPhone: string;
     accountName: string | null;
     isPrimary: boolean;
     isActive: boolean;
   };
   templates?: WhatsAppTemplate[];
   stats?: {
     total: number;
     approved: number;
     pending: number;
     rejected: number;
     totalUsage: number;
   };
 }>(url)
 }

 getWhatsAppTemplatesStats() {
 return this.get<TemplatesStats>('/whatsapp/templates/stats')
 }

 syncWhatsAppTemplates() {
 return this.post<WhatsAppTemplate[]>('/whatsapp/templates/sync', {})
 }

 createWhatsAppTemplate(data: {
 name: string
 language: string
 category: string
 components: any
 }) {
 return this.post<WhatsAppTemplate>('/whatsapp/templates', data)
 }

 // ============================================================
 // LEAD DISTRIBUTION METHODS
 // ============================================================

 getLeadDistributionConfig() {
 return this.get<LeadDistributionConfig>('/distribution/config')
 }

 createLeadDistributionConfig(data: {
 strategy: 'ROUND_ROBIN' | 'WEIGHTED' | 'TERRITORY' | 'MANUAL'
 trigger?: 'ON_CREATE' | 'ON_STATUS_CHANGE' | 'MANUAL'
 isActive?: boolean
 autoReassign?: boolean
 sources?: string[]
 statuses?: string[]
 maxLeadsPerUser?: number
 distributionHours?: string
 }) {
 return this.post<LeadDistributionConfig>('/distribution/config', data)
 }

 updateLeadDistributionConfig(data: {
 strategy?: 'ROUND_ROBIN' | 'WEIGHTED' | 'TERRITORY' | 'MANUAL'
 trigger?: 'ON_CREATE' | 'ON_STATUS_CHANGE' | 'MANUAL'
 isActive?: boolean
 autoReassign?: boolean
 sources?: string[]
 statuses?: string[]
 maxLeadsPerUser?: number
 distributionHours?: string
 }) {
 return this.patch<LeadDistributionConfig>('/distribution/config', data)
 }

 getLeadDistributionRules() {
 return this.get<LeadDistributionRule[]>('/distribution/rules')
 }

 createLeadDistributionRule(data: {
 userId: string
 weight?: number
 priority?: number
 maxLeads?: number
 territories?: any
 isActive?: boolean
 }) {
 return this.post<LeadDistributionRule>('/distribution/rules', data)
 }

 updateLeadDistributionRule(
 id: string,
 data: {
 weight?: number
 priority?: number
 maxLeads?: number
 territories?: any
 isActive?: boolean
 },
 ) {
 return this.patch<LeadDistributionRule>(`/distribution/rules/${id}`, data)
 }

 deleteLeadDistributionRule(id: string) {
 return this.delete(`/distribution/rules/${id}`)
 }

 distributeLeadManually(leadId: string, userId?: string) {
 return this.post<any>('/distribution/distribute', { leadId, userId })
 }

 reassignLead(leadId: string, userId: string) {
 return this.post<any>('/distribution/reassign', { leadId, userId })
 }

 getLeadDistributionStats() {
 return this.get<LeadDistributionStats>('/distribution/stats')
 }

 // ============================================================
 // NOTIFICATION METHODS
 // ============================================================

 getNotifications(params?: {
 page?: number
 limit?: number
 isRead?: boolean
 }) {
 const query = new URLSearchParams()
 if (params?.page) query.append('page', params.page.toString())
 if (params?.limit) query.append('limit', params.limit.toString())
 if (params?.isRead !== undefined) query.append('isRead', params.isRead.toString())

 return this.get<{
 data: Notification[]
 total: number
 unreadCount?: number
 }>(`/notifications?${query.toString()}`)
 }

 getUnreadNotificationsCount() {
 return this.get<{ count: number }>('/notifications/unread-count')
 }

 markNotificationAsRead(notificationId: string) {
 return this.patch(`/notifications/${notificationId}/read`, {})
 }

 markAllNotificationsAsRead() {
 return this.patch('/notifications/read-all', {})
 }

 deleteNotification(notificationId: string) {
 return this.delete(`/notifications/${notificationId}`)
 }

 deleteAllNotifications() {
 return this.delete('/notifications')
 }

 // ============================================================
 // META OAUTH - Credenciais seguras no backend
 // ============================================================

 /**
  * Obtém a URL do OAuth da Meta gerada pelo backend
  * O backend injeta APP_ID e outras credenciais sensíveis
  */
 getMetaOAuthUrl() {
 return this.get<{ url: string }>('/auth/meta/oauth-url')
 }

 /**
  * Busca histórico de tentativas de conexão OAuth
  */
 getWhatsAppOAuthLogs(limit = 10) {
 return this.get<{ logs: WhatsAppOAuthLog[] }>(`/auth/meta/oauth-logs?limit=${limit}`)
 }

 /**
  * Obtém status de todas as integrações do marketplace
  */
 getMarketplaceStatus() {
 return this.get<{
 integrations: Array<{
 id: string
 platform: string
 connected: boolean
 isActive: boolean
 lastSyncAt?: string | null
 lastError?: string | null
 phoneNumberId?: string
 businessAccountId?: string
 createdAt: string
 updatedAt: string
 }>
 }>('/integrations')
 }

 /**
  * Obtém a URL do OAuth do Mercado Livre gerada pelo backend
  */
 getMercadoLivreOAuthUrl() {
 return this.get<{ url: string }>('/auth/mercadolivre/oauth-url')
 }

 /**
  * Obtém status da conexão com Mercado Livre
  */
 getMercadoLivreStatus() {
 return this.get<{
 connected: boolean
 userId?: string
 lastSyncAt?: string | null
 lastError?: string | null
 configuredAt?: string
 }>('/auth/mercadolivre/status')
 }

 disconnectMercadoLivre() {
 return this.delete<{ message: string }>('/auth/mercadolivre/disconnect')
 }

 /**
  * Verifica o status da conexão WhatsApp via OAuth
  */
 getMetaOAuthStatus() {
 return this.get<{
 connected: boolean
 phoneNumberId?: string
 businessAccountId?: string
 lastSyncAt?: string
 lastError?: string
 configuredAt?: string
 message?: string
 }>('/auth/meta/status')
 }

 /**
  * Valida CPF ou CNPJ (rota pública com rate limit)
  */
 validateDocument(document: string) {
 return this.post<{
 valid: boolean
 type: 'cpf' | 'cnpj' | 'invalid'
 message: string
 }>('/public/validation/document', { document })
 }
}

// Instância singleton do serviço
export const apiService = new ApiService()
